import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import {
  getNativeAppsFlyerId,
  setNativeAppsFlyerCustomerUserId,
} from '@/utils/nativeAppsFlyerAttribution';
import { getRevenueCatAttributionContext } from '@/utils/acquisitionAttribution';
import { getNativeAdvertisingIdentifier } from '@/utils/nativeTrackingTransparency';
import { resolveAdIdentityPermission, trackEvent } from '@/utils/trackingManager';
import { env } from '@/lib/env';
import { formatAnnualSavings } from '@/utils/annualSavings';

type CustomerInfo = any;
type PurchasesOffering = any;
type PurchasesPackage = any;
type PurchasesStoreProduct = any;
type SubscriptionOption = any;
type PurchasesStoreProductDiscount = any;
type PurchasesPromotionalOffer = any;

export interface RevenueCatModule {
  PACKAGE_TYPE: {
    ANNUAL: string;
    MONTHLY: string;
    WEEKLY: string;
  };
  PRODUCT_CATEGORY: {
    SUBSCRIPTION: string;
  };
  // Google Play replacement modes (purchases-capacitor 11.x): DEFERRED for
  // the annual save-offer switch, IMMEDIATE_WITHOUT_PRORATION for the monthly
  // offer on the same base plan.
  PRORATION_MODE?: {
    IMMEDIATE_AND_CHARGE_FULL_PRICE: number;
    DEFERRED: number;
    IMMEDIATE_WITHOUT_PRORATION?: number;
    IMMEDIATE_AND_CHARGE_PRORATED_PRICE?: number;
  };
  Purchases: {
    isConfigured: () => Promise<{ isConfigured: boolean }>;
    configure: (options: { apiKey: string; appUserID: string }) => Promise<void>;
    getAppUserID: () => Promise<{ appUserID: string }>;
    logIn: (options: { appUserID: string }) => Promise<unknown>;
    getCustomerInfo: () => Promise<{ customerInfo: CustomerInfo }>;
    restorePurchases: () => Promise<{ customerInfo: CustomerInfo }>;
    syncPurchases: () => Promise<void>;
    setEmail: (options: { email: string | null }) => Promise<void>;
    setDisplayName: (options: { displayName: string | null }) => Promise<void>;
    setAttributes: (attributes: { [key: string]: string | null }) => Promise<void>;
    collectDeviceIdentifiers: () => Promise<void>;
    enableAdServicesAttributionTokenCollection?: () => Promise<void>;
    setAppsflyerID: (options: { appsflyerID: string | null }) => Promise<void>;
    setMediaSource: (options: { mediaSource: string | null }) => Promise<void>;
    setCampaign: (options: { campaign: string | null }) => Promise<void>;
    setAdGroup: (options: { adGroup: string | null }) => Promise<void>;
    setAd: (options: { ad: string | null }) => Promise<void>;
    setCreative: (options: { creative: string | null }) => Promise<void>;
    setKeyword: (options: { keyword: string | null }) => Promise<void>;
    syncAttributesAndOfferingsIfNeeded: () => Promise<unknown>;
    canMakePayments: () => Promise<{ canMakePayments: boolean }>;
    getOfferings: () => Promise<{
      current: PurchasesOffering | null;
      all?: Record<string, PurchasesOffering>;
    } & Record<string, unknown>>;
    purchasePackage: (options: { aPackage: PurchasesPackage }) => Promise<{ customerInfo: CustomerInfo }>;
    purchaseSubscriptionOption: (options: {
      subscriptionOption: SubscriptionOption;
      googleProductChangeInfo?: {
        oldProductIdentifier: string;
        prorationMode?: number;
      } | null;
    }) => Promise<{ customerInfo: CustomerInfo }>;
    getProducts: (options: {
      productIdentifiers: string[];
      type: string;
    }) => Promise<{ products: PurchasesStoreProduct[] }>;
    purchaseStoreProduct: (options: {
      product: PurchasesStoreProduct;
    }) => Promise<{ customerInfo: CustomerInfo }>;
    // iOS only (purchases-capacitor 11.x): RevenueCat signs the Apple
    // promotional offer, then the discounted purchase redeems it.
    getPromotionalOffer?: (options: {
      product: PurchasesStoreProduct;
      discount: PurchasesStoreProductDiscount;
    }) => Promise<PurchasesPromotionalOffer | undefined | null>;
    purchaseDiscountedProduct?: (options: {
      product: PurchasesStoreProduct;
      discount: PurchasesPromotionalOffer;
    }) => Promise<{ customerInfo: CustomerInfo }>;
    checkTrialOrIntroductoryPriceEligibility: (options: {
      productIdentifiers: string[];
    }) => Promise<Record<string, { status?: number; description?: string }>>;
  };
}

export type NativeBillingTier = 'weekly' | 'monthly' | 'annual';
export interface NativeSubscriptionPurchaseOptions {
  /**
   * Google Play only. Direct-paid checkout must select the base plan instead
   * of allowing RevenueCat to choose an eligible introductory offer.
   */
  googleOfferMode?: 'default' | 'base_plan';
  /**
   * Cross-platform paid-first routing. The Android branch must use the
   * dedicated $49.99 product, never the legacy $97 annual base plan.
   */
  nativeOfferMode?: 'default' | 'paid_first_no_trial';
  /**
   * Apple only. Paid-first checkout must purchase the exact approved products
   * that have no introductory offer, never a trial-bearing offering package.
   */
  /**
   * iOS product routing for immediate-charge checkout.
   * - 'no_trial': the bridge's trial-ineligible path. Prefers the dedicated
   *   no-trial SKU and falls back to the standard annual product (Apple omits
   *   the intro offer for ineligible accounts), same as before Aug 18 2026.
   * - 'paid_first_no_trial': the paid-first arm. ONLY the dedicated no-trial
   *   SKU, and any product carrying an intro offer is refused, so "Billed
   *   today" copy can never start a trial.
   */
  iosOfferMode?: 'default' | 'no_trial' | 'paid_first_no_trial';
}
export type NativeSubscriptionStatus = 'active' | 'past_due' | 'canceled' | 'trialing' | null;
export type NativeIntroOfferEligibility = 'eligible' | 'ineligible' | 'no_offer' | 'unknown' | 'unsupported';

export interface NativePaidFirstPlanQuote {
  annualPriceString: string;
  annualReferencePriceString: string | null;
  annualPerWeekString: string | null;
  weeklyPriceString: string;
  weeklyAnnualizedString: string | null;
  weeklyPerDayString: string | null;
  savingsPercent: number;
}

export interface NativePaidFirstRescueQuote {
  annualPriceString: string;
  productIdentifier: string;
  trialDays: 7;
}

export interface NativeAnnualTrialMonthlyQuote {
  annualPriceString: string;
  annualPerMonthString: string | null;
  /** Null when this account is not eligible for the configured annual trial. */
  annualTrialDays: 7 | null;
  monthlyPriceString: string;
  savingsPercent: number;
  /** 12x monthly minus annual, whole units in the storefront currency ("$47"); null = hide. */
  annualSavingsString?: string | null;
}

/** Exact store facts for the immediate-charge Annual + Monthly offer. */
export interface NativeAnnualMonthlyPaidQuote {
  annualPriceString: string;
  annualPerMonthString: string | null;
  monthlyPriceString: string;
  annualPrice: number;
  monthlyPrice: number;
  currencyCode: string;
  savingsPercent: number;
  /** 12x monthly minus annual, whole units in the storefront currency ("$94"); null = hide. */
  annualSavingsString?: string | null;
}

export interface NativeSubscriptionSnapshot {
  isActive: boolean;
  tier: 'free' | 'premium' | 'trialing';
  status: NativeSubscriptionStatus;
  billingTier: NativeBillingTier | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  managementUrl: string | null;
  store: string | null;
  productIdentifier: string | null;
  /** RevenueCat entitlement periodType (INTRO, TRIAL, NORMAL, PREPAID), when known. */
  periodType?: string | null;
}

const DEFAULT_ENTITLEMENT_ID = 'premium';
const IOS_SUBSCRIPTION_MANAGEMENT_URL = 'https://apps.apple.com/account/subscriptions';
const ANDROID_SUBSCRIPTION_MANAGEMENT_URL = 'https://play.google.com/store/account/subscriptions';
const RETIRED_PURCHASE_PRODUCT_ID_PREFIXES = ['226877', '226878'];
const DEFAULT_CURRENT_IOS_ANNUAL_PRODUCT_IDS = [
  'pevr2wv0gtxs.p3gfapng2app.93103.month',
];
// Paid-first uses a dedicated $49.99 SKU so lowering the public offer never
// reprices subscribers who bought the prior $97 no-trial product. The bridge
// annual (93103.month) carries a 14-day introductory offer, and Apple
// auto-applies a product's intro offer to every eligible purchase, so neither
// legacy product can be sold under the new "Charged today" terms.
const IOS_LEGACY_NO_TRIAL_ANNUAL_PRODUCT_ID = 'jp_premium_annual_no_trial';
export const IOS_PAID_FIRST_ANNUAL_PRODUCT_IDS = ['jp_premium_annual_4999'];
export const NATIVE_ANNUAL_SAVE_OFFER_PRODUCT_ID =
  IOS_PAID_FIRST_ANNUAL_PRODUCT_IDS[0];
export const ANDROID_PAID_FIRST_ANNUAL_PRODUCT_ID =
  'jp_premium_annual_4999';
export const ANDROID_PAID_FIRST_ANNUAL_BASE_PLAN_ID = 'p1y';
export const ANDROID_PAID_FIRST_WEEKLY_PRODUCT_ID = 'jp_premium_weekly';
export const ANDROID_PAID_FIRST_WEEKLY_BASE_PLAN_ID = 'p1w';
// The live $97 annual is fetched for localized reference-price display only.
// It is never a paid-first purchase fallback because it carries a trial.
const IOS_PAID_FIRST_REFERENCE_ANNUAL_PRODUCT_ID =
  DEFAULT_CURRENT_IOS_ANNUAL_PRODUCT_IDS[0];
// The bridge's own immediate-charge path (trial-ineligible accounts) keeps the
// original $97 no-trial SKU first, then the standard annual fallback. It must
// never resolve the dedicated $49.99 paid-first product while bridge copy is
// active.
export const IOS_NO_TRIAL_ANNUAL_PRODUCT_IDS = [
  IOS_LEGACY_NO_TRIAL_ANNUAL_PRODUCT_ID,
  'pevr2wv0gtxs.p3gfapng2app.93103.month',
];
export const IOS_PAID_FIRST_WEEKLY_PRODUCT_ID = 'jp_premium_weekly';
export const IOS_PAID_FIRST_RESCUE_ANNUAL_PRODUCT_ID =
  'jp_premium_annual_trial7';
export const PAID_FIRST_RESCUE_TRIAL_DAYS = 7;
export const IOS_ONBOARDING_MONTHLY_NO_TRIAL_PRODUCT_ID =
  'pevr2wv0gtxs.p3gfapng2app.92624.monthly';
const ANDROID_ONBOARDING_MONTHLY_PRODUCT_ID =
  'pevr2wv0gtxs.p3gfapng2app.92624.monthly';
const ANDROID_ONBOARDING_MONTHLY_BASE_PLAN_ID = '92624';
// RETIRED Aug 18 2026 (annual + weekly no-trial experiment, Aug 12-17):
// no purchase or quote path may reference these again. They stay in the
// recognition lists only so the few subscriptions bought during the test keep
// resolving to the right tier until they lapse.
const RETIRED_ANNUAL_3999_PRODUCT_IDS = [
  'jp_premium_annual_3999',
  'jp_premium_annual_3999:p1y',
];
const RETIRED_WEEKLY_999_PRODUCT_IDS = [
  'jp_premium_weekly_999',
  'jp_premium_weekly_999:p1w',
];
const DEFAULT_CURRENT_ANDROID_ANNUAL_PRODUCT_IDS = [
  'pevr2wv0gtxs.p3gfapng2app.93103.month:p1y',
];
const DEFAULT_CURRENT_IOS_MONTHLY_PRODUCT_IDS = [
  'pevr2wv0gtxs.p3gfapng2app.92624.monthly',
];
const DEFAULT_CURRENT_ANDROID_MONTHLY_PRODUCT_IDS = [
  'pevr2wv0gtxs.p3gfapng2app.92624.monthly:92624',
];
const DEFAULT_RECOGNIZED_ANNUAL_PRODUCT_IDS = [
  ...DEFAULT_CURRENT_ANDROID_ANNUAL_PRODUCT_IDS,
  ...DEFAULT_CURRENT_IOS_ANNUAL_PRODUCT_IDS,
  IOS_LEGACY_NO_TRIAL_ANNUAL_PRODUCT_ID,
  ...IOS_PAID_FIRST_ANNUAL_PRODUCT_IDS,
  IOS_PAID_FIRST_RESCUE_ANNUAL_PRODUCT_ID,
  ANDROID_PAID_FIRST_ANNUAL_PRODUCT_ID,
  `${ANDROID_PAID_FIRST_ANNUAL_PRODUCT_ID}:${ANDROID_PAID_FIRST_ANNUAL_BASE_PLAN_ID}`,
  ...RETIRED_ANNUAL_3999_PRODUCT_IDS,
  'jp_premium_annual_offer67',
  'jp_premium_annual_offer67:p1y',
  'jp_premium_annual_offer29',
  'jp_premium_annual_offer29:p1y',
  '226878.monthly',
];
const DEFAULT_RECOGNIZED_MONTHLY_PRODUCT_IDS = [
  '226877.monthly',
  IOS_ONBOARDING_MONTHLY_NO_TRIAL_PRODUCT_ID,
  ...DEFAULT_CURRENT_ANDROID_MONTHLY_PRODUCT_IDS,
  ...DEFAULT_CURRENT_IOS_MONTHLY_PRODUCT_IDS,
];
const APPSFLYER_ID_RETRY_COUNT = 5;
const APPSFLYER_ID_RETRY_DELAY_MS = 400;
let appsFlyerIdRetryDelayMs = APPSFLYER_ID_RETRY_DELAY_MS;
const NATIVE_PURCHASE_CONFIGURATION_ERROR_MESSAGE =
  'In-app purchases are not available in this app version yet. Please try again soon.';

let configurePromise: Promise<boolean> | null = null;
let configuredForUserId: string | null = null;
let revenueCatModulePromise: Promise<RevenueCatModule | null> | null = null;
let lastSyncedCustomerAttributesKey: string | null = null;
let lastSyncedAttributionKey: string | null = null;

export interface NativeCustomerAttributesInput {
  email?: string | null;
  displayName?: string | null;
}

export interface NativeCheckoutAttributionInput {
  offerKind?: string | null;
  paywallFeature?: string | null;
  /** Offer arm for the measurement ledger, e.g. 'annual_led' | 'bridge'. */
  offerConfig?: string | null;
  /** First-open session key so revenue can be cohorted by install. */
  installSession?: string | null;
}

class NativePurchaseConfigurationError extends Error {
  constructor(message = NATIVE_PURCHASE_CONFIGURATION_ERROR_MESSAGE) {
    super(message);
    this.name = 'NativePurchaseConfigurationError';
  }
}

function parseProductIdList(raw: string | undefined): string[] {
  if (!raw) return [];
  return raw
    .split(/[,\n]/)
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);
}

function parseDirectPurchaseProductIdList(raw: string | undefined): string[] {
  if (!raw) return [];
  return raw
    .split(/[,\n]/)
    .map((value) => value.trim())
    .filter(Boolean);
}

const KNOWN_ANNUAL_PRODUCT_IDS = Array.from(
  new Set([
    ...DEFAULT_RECOGNIZED_ANNUAL_PRODUCT_IDS,
    ...parseProductIdList(env.revenueCatNativeAnnualProductIds),
    ...parseProductIdList(env.revenueCatAndroidAnnualProductIds),
    ...parseProductIdList(env.revenueCatIosAnnualProductIds),
  ])
);

const KNOWN_MONTHLY_PRODUCT_IDS = Array.from(
  new Set([
    ...DEFAULT_RECOGNIZED_MONTHLY_PRODUCT_IDS,
    ...parseProductIdList(env.revenueCatNativeMonthlyProductIds),
    ...parseProductIdList(env.revenueCatAndroidMonthlyProductIds),
    ...parseProductIdList(env.revenueCatIosMonthlyProductIds),
  ])
);

// No default weekly product ids: the weekly plan ships behind these env vars
// once the store products exist, so older configs simply never see weekly.
const KNOWN_WEEKLY_PRODUCT_IDS = Array.from(
  new Set([
    IOS_PAID_FIRST_WEEKLY_PRODUCT_ID,
    ...RETIRED_WEEKLY_999_PRODUCT_IDS,
    ...parseProductIdList(env.revenueCatNativeWeeklyProductIds),
    ...parseProductIdList(env.revenueCatAndroidWeeklyProductIds),
    ...parseProductIdList(env.revenueCatIosWeeklyProductIds),
  ])
);

export function matchesKnownProductIdExact(productIdentifier: string, knownProductIds: string[]): boolean {
  const normalized = productIdentifier.toLowerCase();
  return knownProductIds.some((known) => normalized === known || normalized.endsWith(`.${known}`));
}

export function matchesKnownProductId(productIdentifier: string, knownProductIds: string[]): boolean {
  const normalized = productIdentifier.toLowerCase();
  const normalizedBase = normalized.split(':')[0];
  const inputHasBasePlan = normalized.includes(':');
  return knownProductIds.some((known) => {
    const knownBase = known.split(':')[0];
    // Google Play ships several base plans under one subscription product
    // (product:p1y vs product:p1w). When both ids carry a base plan,
    // product-level matching would classify every base plan as whichever
    // tier list is checked first, so require the full id to match.
    if (inputHasBasePlan && known.includes(':')) {
      return normalized === known || normalized.endsWith(`.${known}`);
    }
    return (
      normalized === known ||
      normalizedBase === known ||
      normalizedBase === knownBase ||
      normalized.endsWith(`.${known}`) ||
      normalizedBase.endsWith(`.${known}`) ||
      normalizedBase.endsWith(`.${knownBase}`)
    );
  });
}

function hasFutureExpiration(value: string | null | undefined): boolean {
  if (!value) return false;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) && timestamp > Date.now();
}

function isNativePlatform(): boolean {
  return Capacitor.isNativePlatform();
}

function getNativePlatform(): string {
  return Capacitor.getPlatform();
}

function getRevenueCatApiKeyForCurrentPlatform(): string | null {
  const platform = getNativePlatform();
  if (platform === 'ios') {
    return env.revenueCatAppleApiKey || env.revenueCatApiKey || null;
  }
  if (platform === 'android') {
    return env.revenueCatGoogleApiKey || env.revenueCatApiKey || null;
  }
  return null;
}

async function loadRevenueCatModule(): Promise<RevenueCatModule | null> {
  if (!isNativePlatform() || typeof window === 'undefined') {
    return null;
  }

  if (!revenueCatModulePromise) {
    revenueCatModulePromise = import('@revenuecat/purchases-capacitor')
      .then((module) => module as unknown as RevenueCatModule)
      .catch((error) => {
        console.error('Failed to load RevenueCat native billing module:', error);
        revenueCatModulePromise = null;
        return null;
      });
  }

  return revenueCatModulePromise;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

// Unit tests run with a fake AppsFlyer that has no id, so every RevenueCat
// setup would wait out all five real retries. Tests set the delay to 0.
export function setAppsFlyerIdRetryDelayForTests(ms: number): void {
  appsFlyerIdRetryDelayMs = ms;
}

async function getNativeAppsFlyerIdWithRetry(): Promise<string | null> {
  let lastError: unknown = null;

  for (let attempt = 0; attempt < APPSFLYER_ID_RETRY_COUNT; attempt += 1) {
    try {
      const appsflyerId = await getNativeAppsFlyerId();
      if (appsflyerId) return appsflyerId;
    } catch (error) {
      lastError = error;
    }

    if (attempt < APPSFLYER_ID_RETRY_COUNT - 1) {
      await sleep(appsFlyerIdRetryDelayMs);
    }
  }

  if (lastError) {
    console.error('Failed to read native AppsFlyer id for RevenueCat attribution:', lastError);
  }

  return null;
}

async function clearRevenueCatAdIdentity(Purchases: RevenueCatModule['Purchases']): Promise<void> {
  try {
    await Purchases.setAppsflyerID({ appsflyerID: null });
    await Purchases.setAttributes({
      $appsflyerId: null,
      appsflyer_id: null,
      $idfa: null,
      idfa: null,
    });
    await Purchases.syncAttributesAndOfferingsIfNeeded();
  } catch (error) {
    console.error('Failed to clear RevenueCat ad identifiers for an opted-out member:', error);
  }
}

async function syncRevenueCatAttributionIdentifiers(
  userId: string,
  Purchases: RevenueCatModule['Purchases'],
  options: { force?: boolean } = {}
): Promise<void> {
  if (!['android', 'ios'].includes(getNativePlatform())) return;

  // RevenueCat sends native subscription events to AppsFlyer whenever it
  // holds $appsflyerId, so the ad data policy applies here: an opted-out
  // member (GPC, an explicit opt-out on this device, or the account's
  // ad_data_opt_out) gets no AppsFlyer link and any earlier one is cleared.
  // An unreadable account skips the setup for this session without clearing.
  // An ATT decline alone is not an opt-out.
  const permission = await resolveAdIdentityPermission(userId).catch(
    (): 'unknown' => 'unknown'
  );
  if (permission !== 'allowed') {
    lastSyncedAttributionKey = null;
    if (permission === 'opted_out') await clearRevenueCatAdIdentity(Purchases);
    return;
  }

  await setNativeAppsFlyerCustomerUserId(userId).catch((error) => {
    console.error('Failed to set AppsFlyer customer user id:', error);
  });

  const appsflyerId = await getNativeAppsFlyerIdWithRetry();
  const nativeAdvertising =
    getNativePlatform() === 'ios'
      ? await getNativeAdvertisingIdentifier().catch((error) => {
          console.error('Failed to read iOS advertising identifiers for RevenueCat attribution:', error);
          return null;
        })
      : null;
  const idfa = nativeAdvertising?.idfa || null;
  const idfv = nativeAdvertising?.idfv || null;
  const trackingConsentStatus = nativeAdvertising?.status || null;
  const revenueCatAttribution = getRevenueCatAttributionContext();
  const cacheKey = JSON.stringify({ userId, appsflyerId, idfa, idfv, trackingConsentStatus, revenueCatAttribution });
  if (!options.force && lastSyncedAttributionKey === cacheKey) return;

  if (getNativePlatform() === 'ios' && Purchases.enableAdServicesAttributionTokenCollection) {
    await Purchases.enableAdServicesAttributionTokenCollection().catch((error) => {
      console.error('Failed to enable RevenueCat Apple Search Ads attribution:', error);
    });
  }

  await Purchases.collectDeviceIdentifiers();

  if (appsflyerId) {
    await Purchases.setAppsflyerID({ appsflyerID: appsflyerId });
  }

  const attributionAttributes: Record<string, string | null> = {};
  if (appsflyerId) {
    attributionAttributes.$appsflyerId = appsflyerId;
    attributionAttributes.appsflyer_id = appsflyerId;
  }
  if (idfa) {
    attributionAttributes.$idfa = idfa;
    attributionAttributes.idfa = idfa;
  }
  if (idfv) {
    attributionAttributes.$idfv = idfv;
    attributionAttributes.idfv = idfv;
  }
  if (trackingConsentStatus) {
    attributionAttributes.tracking_consent_status = trackingConsentStatus;
  }
  if (revenueCatAttribution) {
    const { mediaSource, campaign, adGroup, ad, creative, keyword } = revenueCatAttribution;
    if (mediaSource) {
      attributionAttributes.media_source = mediaSource;
      attributionAttributes.af_media_source = mediaSource;
      attributionAttributes.acquisition_source = mediaSource;
    }
    if (campaign) {
      attributionAttributes.campaign = campaign;
      attributionAttributes.af_campaign = campaign;
      attributionAttributes.acquisition_campaign = campaign;
    }
    if (adGroup) {
      attributionAttributes.adset = adGroup;
      attributionAttributes.af_adset = adGroup;
      attributionAttributes.acquisition_ad_group = adGroup;
    }
    if (ad) {
      attributionAttributes.ad = ad;
      attributionAttributes.af_ad = ad;
      attributionAttributes.acquisition_ad = ad;
    }
    if (creative) {
      attributionAttributes.creative = creative;
      attributionAttributes.utm_content = creative;
    }
    if (keyword) {
      attributionAttributes.keyword = keyword;
      attributionAttributes.utm_term = keyword;
    }
    attributionAttributes.native_revenuecat_attribution_synced_at = new Date().toISOString();

    // The RevenueCat dashboard's attribution card and chart filters read only the
    // reserved $-prefixed attributes, which the SDK exposes through dedicated setters.
    try {
      if (mediaSource) await Purchases.setMediaSource({ mediaSource });
      if (campaign) await Purchases.setCampaign({ campaign });
      if (adGroup) await Purchases.setAdGroup({ adGroup });
      if (ad) await Purchases.setAd({ ad });
      if (creative) await Purchases.setCreative({ creative });
      if (keyword) await Purchases.setKeyword({ keyword });
    } catch (error) {
      console.error('Failed to set RevenueCat reserved attribution attributes:', error);
    }
  }
  if (Object.keys(attributionAttributes).length > 0) {
    await Purchases.setAttributes(attributionAttributes);
  }

  await Purchases.syncAttributesAndOfferingsIfNeeded();
  lastSyncedAttributionKey = cacheKey;
}

function getPackageForTier(
  offering: PurchasesOffering,
  tier: NativeBillingTier,
  packageType: RevenueCatModule['PACKAGE_TYPE']
): PurchasesPackage | null {
  if (tier === 'annual') {
    if (offering.annual) return offering.annual;
    const annualPackage = offering.availablePackages.find(
      (pkg: PurchasesPackage) => pkg.packageType === packageType.ANNUAL
    );
    if (annualPackage) return annualPackage;
    return (
      offering.availablePackages.find((pkg: PurchasesPackage) =>
        /annual|year|yr|12[_-]?month|12m/i.test(pkg.identifier) ||
        /annual|year|yr|12[_-]?month|12m/i.test(pkg.product.identifier)
      ) || null
    );
  }

  if (tier === 'weekly') {
    if (offering.weekly) return offering.weekly;
    const weeklyPackage = offering.availablePackages.find(
      (pkg: PurchasesPackage) => pkg.packageType === packageType.WEEKLY
    );
    if (weeklyPackage) return weeklyPackage;
    return (
      offering.availablePackages.find((pkg: PurchasesPackage) =>
        /weekly|week|1[_-]?week|1w\b/i.test(pkg.identifier) ||
        /weekly|week|1[_-]?week|1w\b/i.test(pkg.product.identifier)
      ) || null
    );
  }

  if (offering.monthly) return offering.monthly;
  const monthlyPackage = offering.availablePackages.find(
    (pkg: PurchasesPackage) => pkg.packageType === packageType.MONTHLY
  );
  if (monthlyPackage) return monthlyPackage;
  return (
    offering.availablePackages.find((pkg: PurchasesPackage) =>
      /monthly|month|mo|1[_-]?month|1m/i.test(pkg.identifier) ||
      /monthly|month|mo|1[_-]?month|1m/i.test(pkg.product.identifier)
    ) || null
  );
}

function inferBillingTierFromProductId(productIdentifier: string | null | undefined): NativeBillingTier | null {
  if (!productIdentifier) return null;
  // Exact full ids win first so a Play base plan (product:p1w) can never be
  // claimed by another tier's product-level match on the shared product.
  if (matchesKnownProductIdExact(productIdentifier, KNOWN_ANNUAL_PRODUCT_IDS)) return 'annual';
  if (matchesKnownProductIdExact(productIdentifier, KNOWN_WEEKLY_PRODUCT_IDS)) return 'weekly';
  if (matchesKnownProductIdExact(productIdentifier, KNOWN_MONTHLY_PRODUCT_IDS)) return 'monthly';
  if (matchesKnownProductId(productIdentifier, KNOWN_ANNUAL_PRODUCT_IDS)) return 'annual';
  if (matchesKnownProductId(productIdentifier, KNOWN_WEEKLY_PRODUCT_IDS)) return 'weekly';
  if (matchesKnownProductId(productIdentifier, KNOWN_MONTHLY_PRODUCT_IDS)) return 'monthly';
  const normalized = productIdentifier.toLowerCase();
  if (/(annual|year|yr|12[_-]?month|12m)/.test(normalized)) return 'annual';
  // Weekly before monthly: the monthly pattern's bare "mo" would otherwise
  // never let a "weekly"/"1w" product id reach this branch.
  if (/(weekly|week|1[_-]?week|1w\b)/.test(normalized)) return 'weekly';
  if (/(monthly|month|mo|1[_-]?month|1m)/.test(normalized)) return 'monthly';
  return null;
}

function toGooglePlayBasePlanFormat(productIdentifier: string): string {
  if (productIdentifier.includes(':')) return productIdentifier;
  return productIdentifier;
}

export function isRetiredNativePurchaseProductId(productIdentifier: string): boolean {
  const normalized = productIdentifier.toLowerCase();
  return RETIRED_PURCHASE_PRODUCT_ID_PREFIXES.some(
    (prefix) => normalized === prefix || normalized.startsWith(`${prefix}.`) || normalized.startsWith(`${prefix}:`)
  );
}

export function getDirectProductIdentifiersForTier(
  tier: NativeBillingTier,
  options: NativeSubscriptionPurchaseOptions = {},
): string[] {
  const nativeIdentifiers =
    tier === 'annual'
      ? parseDirectPurchaseProductIdList(env.revenueCatPurchaseNativeAnnualProductIds)
      : tier === 'weekly'
        ? parseDirectPurchaseProductIdList(env.revenueCatPurchaseNativeWeeklyProductIds)
        : parseDirectPurchaseProductIdList(env.revenueCatPurchaseNativeMonthlyProductIds);
  const platform = getNativePlatform();
  if (platform === 'ios' && options.iosOfferMode === 'paid_first_no_trial') {
    if (tier === 'annual') return IOS_PAID_FIRST_ANNUAL_PRODUCT_IDS;
    if (tier === 'weekly') return [IOS_PAID_FIRST_WEEKLY_PRODUCT_ID];
    throw new NativePurchaseConfigurationError(
      `iOS paid-first checkout does not support the ${tier} tier.`,
    );
  }
  if (
    platform === 'android' &&
    options.nativeOfferMode === 'paid_first_no_trial'
  ) {
    if (tier === 'annual') return [ANDROID_PAID_FIRST_ANNUAL_PRODUCT_ID];
    if (tier === 'weekly') return [ANDROID_PAID_FIRST_WEEKLY_PRODUCT_ID];
    throw new NativePurchaseConfigurationError(
      `Android paid-first checkout does not support the ${tier} tier.`,
    );
  }
  if (platform === 'ios' && options.iosOfferMode === 'no_trial') {
    if (tier === 'annual') return IOS_NO_TRIAL_ANNUAL_PRODUCT_IDS;
    if (tier === 'weekly') return [IOS_PAID_FIRST_WEEKLY_PRODUCT_ID];
    throw new NativePurchaseConfigurationError(
      `iOS no-trial checkout does not support the ${tier} tier.`,
    );
  }
  // Weekly has no default product ids: it only exists once the store products
  // ship and the env vars point at them.
  const platformIdentifiers =
    platform === 'ios'
      ? tier === 'annual'
        ? [
            ...DEFAULT_CURRENT_IOS_ANNUAL_PRODUCT_IDS,
            ...parseDirectPurchaseProductIdList(env.revenueCatPurchaseIosAnnualProductIds),
          ]
        : tier === 'weekly'
          ? parseDirectPurchaseProductIdList(env.revenueCatPurchaseIosWeeklyProductIds)
          : [
              ...DEFAULT_CURRENT_IOS_MONTHLY_PRODUCT_IDS,
              ...parseDirectPurchaseProductIdList(env.revenueCatPurchaseIosMonthlyProductIds),
            ]
      : platform === 'android'
        ? tier === 'annual'
          ? [
              ...DEFAULT_CURRENT_ANDROID_ANNUAL_PRODUCT_IDS,
              ...parseDirectPurchaseProductIdList(env.revenueCatPurchaseAndroidAnnualProductIds),
            ]
          : tier === 'weekly'
            ? parseDirectPurchaseProductIdList(env.revenueCatPurchaseAndroidWeeklyProductIds)
            : [
                ...DEFAULT_CURRENT_ANDROID_MONTHLY_PRODUCT_IDS,
                ...parseDirectPurchaseProductIdList(env.revenueCatPurchaseAndroidMonthlyProductIds),
              ]
        : [];
  const base = Array.from(new Set([...nativeIdentifiers, ...platformIdentifiers]));
  const retiredIdentifiers = base.filter(isRetiredNativePurchaseProductId);
  if (retiredIdentifiers.length > 0) {
    console.error(
      `Ignoring retired direct native ${tier} purchase fallback identifiers: ${retiredIdentifiers.join(', ')}`
    );
  }

  const activeProductIdentifiers = base.filter((identifier) => !isRetiredNativePurchaseProductId(identifier));
  const withPlayBasePlanVariants = activeProductIdentifiers.flatMap((identifier) => [
    identifier,
    toGooglePlayBasePlanFormat(identifier),
  ]);
  return Array.from(new Set(withPlayBasePlanVariants));
}

function isOfferingsConfigurationError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const details = error as {
    message?: string;
    data?: { readableErrorCode?: string; readable_error_code?: string; underlyingErrorMessage?: string };
  };

  const readableCode =
    details.data?.readableErrorCode || details.data?.readable_error_code || '';
  const message = (details.message || '').toLowerCase();
  const underlying = (details.data?.underlyingErrorMessage || '').toLowerCase();

  return (
    readableCode === 'ConfigurationError' ||
    message.includes('configuration') ||
    underlying.includes('no products registered in the revenuecat dashboard for your offerings')
  );
}

function getGooglePlayBasePlanOption(
  product: PurchasesStoreProduct | null | undefined,
  expectedBasePlanId?: string,
): SubscriptionOption | null {
  const options = [
    ...(Array.isArray(product?.subscriptionOptions)
      ? product.subscriptionOptions
      : []),
    product?.defaultOption,
  ].filter(Boolean) as SubscriptionOption[];

  return (
    options.find((option) => {
      if (option.isBasePlan !== true || option.freePhase) return false;
      if (!expectedBasePlanId) return true;
      const optionId = typeof option.id === 'string' ? option.id : '';
      return optionId.split(':')[0] === expectedBasePlanId;
    }) || null
  );
}

async function purchaseNativeSubscriptionFromDirectProducts(
  tier: NativeBillingTier,
  userId: string,
  options: NativeSubscriptionPurchaseOptions,
): Promise<CustomerInfo> {
  const revenueCat = await loadRevenueCatModule();
  if (!revenueCat) {
    throw new Error('Native in-app billing is unavailable in this environment.');
  }

  const { PRODUCT_CATEGORY, Purchases } = revenueCat;
  await assertRevenueCatIdentity(Purchases, userId);
  await syncRevenueCatAttributionIdentifiers(userId, Purchases, { force: true });

  const candidateIdentifiers = getDirectProductIdentifiersForTier(tier, options);
  if (candidateIdentifiers.length === 0) {
    console.error(
      `RevenueCat offerings are unavailable and no new direct native ${tier} purchase product IDs are configured.`
    );
    throw new NativePurchaseConfigurationError();
  }

  let discoveredProducts: PurchasesStoreProduct[] = [];

  // Query identifiers individually so one bad Play product does not poison the entire lookup.
  for (const identifier of candidateIdentifiers) {
    try {
      const { products } = await Purchases.getProducts({
        productIdentifiers: [identifier],
        type: PRODUCT_CATEGORY.SUBSCRIPTION,
      });
      if (products && products.length > 0) {
        discoveredProducts = products;
        break;
      }
    } catch (error) {
      console.warn(`Direct product lookup failed for ${identifier}:`, error);
    }
  }

  if (discoveredProducts.length === 0) {
    console.error(`No direct store products are available for native ${tier} checkout.`);
    throw new NativePurchaseConfigurationError();
  }

  const productByTier = discoveredProducts.find((storeProduct: PurchasesStoreProduct) => {
    return inferBillingTierFromProductId(storeProduct.identifier) === tier;
  });
  const targetProduct = productByTier || discoveredProducts[0];

  if (
    getNativePlatform() === 'ios' &&
    options.iosOfferMode === 'paid_first_no_trial' &&
    hasIosIntroductoryOffer(targetProduct)
  ) {
    // The paywall promised an immediate charge; a product with an intro
    // offer would silently start a trial for eligible accounts.
    console.error(
      `iOS no-trial checkout refused: ${targetProduct.identifier} carries an introductory offer.`,
    );
    throw new NativePurchaseConfigurationError();
  }

  if (
    getNativePlatform() === 'android' &&
    options.googleOfferMode === 'base_plan'
  ) {
    const expectedBasePlanId =
      options.nativeOfferMode === 'paid_first_no_trial'
        ? tier === 'annual'
          ? ANDROID_PAID_FIRST_ANNUAL_BASE_PLAN_ID
          : tier === 'weekly'
            ? ANDROID_PAID_FIRST_WEEKLY_BASE_PLAN_ID
            : undefined
        : undefined;
    const basePlan = getGooglePlayBasePlanOption(
      targetProduct,
      expectedBasePlanId,
    );
    if (!basePlan) {
      console.error(
        `No trial-free Google Play base plan is available for native ${tier} checkout.`,
      );
      throw new NativePurchaseConfigurationError();
    }
    const result = await Purchases.purchaseSubscriptionOption({
      subscriptionOption: basePlan,
    });
    return result.customerInfo;
  }

  const result = await Purchases.purchaseStoreProduct({
    product: targetProduct,
  });
  return result.customerInfo;
}

export function isKnownNativePremiumProductId(productIdentifier: string | null | undefined): boolean {
  if (!productIdentifier) return false;
  return (
    matchesKnownProductId(productIdentifier, KNOWN_ANNUAL_PRODUCT_IDS) ||
    matchesKnownProductId(productIdentifier, KNOWN_MONTHLY_PRODUCT_IDS) ||
    matchesKnownProductId(productIdentifier, KNOWN_WEEKLY_PRODUCT_IDS)
  );
}

function getSubscriptionStatusFromEntitlement(
  isActive: boolean,
  periodType: string | null | undefined,
  hasBillingIssue: boolean
): NativeSubscriptionStatus {
  if (!isActive) return 'canceled';
  if (hasBillingIssue) return 'past_due';
  if ((periodType || '').toUpperCase() === 'TRIAL') return 'trialing';
  return 'active';
}

export function isNativeBillingEnabled(): boolean {
  return isNativePlatform() && Boolean(getRevenueCatApiKeyForCurrentPlatform());
}

export function isNativePurchaseFlowEnabled(): boolean {
  const configured = env.enableNativeSubscriptions;
  return isNativePlatform() && String(configured ?? 'true').toLowerCase() !== 'false';
}

export function getNativeEntitlementId(): string {
  return env.revenueCatPremiumEntitlementId || DEFAULT_ENTITLEMENT_ID;
}

export function getNativeStoreManagementUrlFallback(): string | null {
  const platform = getNativePlatform();
  if (platform === 'ios') return IOS_SUBSCRIPTION_MANAGEMENT_URL;
  if (platform === 'android') return ANDROID_SUBSCRIPTION_MANAGEMENT_URL;
  return null;
}

export function isNativePurchaseCanceledError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const normalized = error as { message?: string; userCancelled?: boolean; code?: string | number };
  if (normalized.userCancelled) return true;
  if (String(normalized.code ?? '') === '1') return true;
  return (normalized.message || '').toLowerCase().includes('cancel');
}

export function isNativePurchaseAlreadyOwnedError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const normalized = error as { message?: string; code?: string | number };
  const message = (normalized.message || '').toLowerCase();
  const code = String(normalized.code ?? '').toLowerCase();

  return (
    message.includes('currently subscribed') ||
    message.includes('already subscribed') ||
    message.includes('already purchased') ||
    code === 'purchase_already_owned' ||
    code === 'already_owned'
  );
}

export function isNativePurchaseConfigurationError(error: unknown): boolean {
  return error instanceof NativePurchaseConfigurationError ||
    (error instanceof Error && error.name === 'NativePurchaseConfigurationError');
}

export async function ensureNativeBillingConfigured(userId: string): Promise<boolean> {
  if (!isNativeBillingEnabled()) return false;
  if (configuredForUserId === userId) return true;
  if (configurePromise) {
    // Another caller is mid-configure, possibly for a different user id.
    // Wait for it, then fall through so THIS user id gets logged in too
    // instead of silently inheriting the other caller's identity.
    await configurePromise.catch(() => false);
    if (configuredForUserId === userId) return true;
  }

  configurePromise = (async () => {
    const apiKey = getRevenueCatApiKeyForCurrentPlatform();
    if (!apiKey) return false;
    const revenueCat = await loadRevenueCatModule();
    if (!revenueCat) return false;

    const { Purchases } = revenueCat;

    const { isConfigured } = await Purchases.isConfigured();
    if (!isConfigured) {
      await Purchases.configure({ apiKey, appUserID: userId });
      configuredForUserId = userId;
      await syncRevenueCatAttributionIdentifiers(userId, Purchases);
      return true;
    }

    const { appUserID } = await Purchases.getAppUserID();
    if (appUserID !== userId) {
      await Purchases.logIn({ appUserID: userId });
    }
    configuredForUserId = userId;
    await syncRevenueCatAttributionIdentifiers(userId, Purchases);
    return true;
  })()
    .catch((error) => {
      console.error('Failed to configure native billing:', error);
      return false;
    })
    .finally(() => {
      configurePromise = null;
    });

  return configurePromise;
}

export async function syncNativeRevenueCatAttribution(
  userId: string,
  options: { force?: boolean } = {}
): Promise<boolean> {
  const configured = await ensureNativeBillingConfigured(userId);
  if (!configured) return false;

  const revenueCat = await loadRevenueCatModule();
  if (!revenueCat) return false;

  await syncRevenueCatAttributionIdentifiers(userId, revenueCat.Purchases, options);
  return true;
}

function normalizeRevenueCatAttribute(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function buildCustomerAttributesCacheKey(
  userId: string,
  attributes: NativeCustomerAttributesInput
): string {
  return JSON.stringify({
    userId,
    email: normalizeRevenueCatAttribute(attributes.email)?.toLowerCase() ?? null,
    displayName: normalizeRevenueCatAttribute(attributes.displayName) ?? null,
  });
}

export async function syncNativeCustomerAttributes(
  userId: string,
  attributes: NativeCustomerAttributesInput
): Promise<boolean> {
  const email = normalizeRevenueCatAttribute(attributes.email)?.toLowerCase() ?? null;
  const displayName = normalizeRevenueCatAttribute(attributes.displayName);
  if (!email && !displayName) return false;

  const cacheKey = buildCustomerAttributesCacheKey(userId, { email, displayName });
  if (lastSyncedCustomerAttributesKey === cacheKey) return true;

  const configured = await ensureNativeBillingConfigured(userId);
  if (!configured) return false;

  const revenueCat = await loadRevenueCatModule();
  if (!revenueCat) return false;

  const { Purchases } = revenueCat;

  if (email) {
    await Purchases.setEmail({ email });
  }

  if (displayName) {
    await Purchases.setDisplayName({ displayName });
  }

  await Purchases.syncAttributesAndOfferingsIfNeeded();
  lastSyncedCustomerAttributesKey = cacheKey;
  return true;
}

export async function syncNativeCheckoutAttribution(
  userId: string,
  attributes: NativeCheckoutAttributionInput
): Promise<boolean> {
  const offerKind = normalizeRevenueCatAttribute(attributes.offerKind);
  const paywallFeature = normalizeRevenueCatAttribute(attributes.paywallFeature);
  const offerConfig = normalizeRevenueCatAttribute(attributes.offerConfig);
  const installSession = normalizeRevenueCatAttribute(attributes.installSession);

  const configured = await ensureNativeBillingConfigured(userId);
  if (!configured) return false;

  const revenueCat = await loadRevenueCatModule();
  if (!revenueCat) return false;

  await revenueCat.Purchases.setAttributes({
    jp_offer_kind: offerKind,
    jp_paywall_feature: paywallFeature,
    // Names are load-bearing: the RevenueCat webhook handler extracts exactly
    // jp_offer_config / jp_install_session into ledger row context.
    jp_offer_config: offerConfig,
    jp_install_session: installSession,
    // RevenueCat subscriber attributes persist across purchases. Clear the
    // timestamp with the context so a checkout without an offer cannot inherit
    // a prior paywall's values on a later transaction.
    jp_checkout_context_captured_at:
      offerKind || paywallFeature ? new Date().toISOString() : null,
  });
  await revenueCat.Purchases.syncAttributesAndOfferingsIfNeeded();
  return true;
}

export async function getNativeCustomerInfo(userId: string): Promise<CustomerInfo | null> {
  const configured = await ensureNativeBillingConfigured(userId);
  if (!configured) return null;
  const revenueCat = await loadRevenueCatModule();
  if (!revenueCat) return null;
  const { Purchases } = revenueCat;
  const { customerInfo } = await Purchases.getCustomerInfo();
  return customerInfo;
}

/**
 * Posts the device's store transactions to RevenueCat without any sign-in
 * prompt (unlike restore). Used after a purchase that StoreKit reports as
 * cancelled because the store account is already subscribed, so the
 * existing subscription can attach to the signed-in account.
 */
export async function syncNativeStorePurchases(userId: string): Promise<void> {
  const configured = await ensureNativeBillingConfigured(userId);
  if (!configured) return;
  const revenueCat = await loadRevenueCatModule();
  if (!revenueCat) return;
  await revenueCat.Purchases.syncPurchases();
}

export async function restoreNativeSubscriptionPurchases(userId: string): Promise<CustomerInfo | null> {
  const configured = await ensureNativeBillingConfigured(userId);
  if (!configured) return null;
  const revenueCat = await loadRevenueCatModule();
  if (!revenueCat) return null;
  const { Purchases } = revenueCat;
  const { customerInfo } = await Purchases.restorePurchases();
  return customerInfo;
}

// A Jul 3 2026 purchase reached RevenueCat under an app user id that was
// never an auth user, so the webhook could not resolve the buyer and the
// subscription never persisted. The exact injection path was not
// reproducible, so enforce the invariant at the moment it matters: no
// purchase proceeds unless the SDK identity equals the auth user id.
async function assertRevenueCatIdentity(
  Purchases: RevenueCatModule['Purchases'],
  userId: string
): Promise<void> {
  const { appUserID } = await Purchases.getAppUserID();
  if (appUserID === userId) return;

  console.error(
    `RevenueCat identity mismatch before purchase: sdk=${appUserID} auth=${userId}; forcing logIn`
  );
  await Purchases.logIn({ appUserID: userId });
  const verified = await Purchases.getAppUserID();
  if (verified.appUserID !== userId) {
    throw new Error('RevenueCat identity mismatch could not be corrected; aborting purchase.');
  }
}

/**
 * Whether the current RevenueCat offering can safely render the paid Annual +
 * Weekly choice. Android requires a trial-free base plan for both products;
 * otherwise the UI fails closed instead of advertising immediate payment.
 */
export async function isNativeWeeklyPlanAvailable(userId: string): Promise<boolean> {
  if (!isNativeBillingEnabled()) return false;

  try {
    const configured = await ensureNativeBillingConfigured(userId);
    if (!configured) return false;
    const revenueCat = await loadRevenueCatModule();
    if (!revenueCat) return false;

    const { PACKAGE_TYPE, Purchases } = revenueCat;
    if (getNativePlatform() === 'ios') {
      const productIdsToCheck = [
        ...IOS_PAID_FIRST_ANNUAL_PRODUCT_IDS,
        IOS_PAID_FIRST_WEEKLY_PRODUCT_ID,
      ];
      const discoveredProductIds = new Set<string>();
      for (const productIdentifier of productIdsToCheck) {
        const { products } = await Purchases.getProducts({
          productIdentifiers: [productIdentifier],
          type: revenueCat.PRODUCT_CATEGORY.SUBSCRIPTION,
        });
        for (const product of products || []) {
          discoveredProductIds.add(product.identifier);
        }
      }
      return (
        IOS_PAID_FIRST_ANNUAL_PRODUCT_IDS.some((productIdentifier) =>
          discoveredProductIds.has(productIdentifier),
        ) && discoveredProductIds.has(IOS_PAID_FIRST_WEEKLY_PRODUCT_ID)
      );
    }

    const offerings = await Purchases.getOfferings();
    const offering = offerings.current;
    if (!offering) return false;

    const weeklyPackage = getPackageForTier(
      offering,
      'weekly',
      PACKAGE_TYPE,
    );
    if (!weeklyPackage) return false;

    if (getNativePlatform() === 'android') {
      const annualPackage = getPackageForTier(
        offering,
        'annual',
        PACKAGE_TYPE,
      );
      return Boolean(
        getGooglePlayBasePlanOption(weeklyPackage.product) &&
          getGooglePlayBasePlanOption(annualPackage?.product),
      );
    }

    return true;
  } catch (error) {
    console.warn('Weekly plan availability check failed:', error);
    return false;
  }
}

function hasIosIntroductoryOffer(
  product: PurchasesStoreProduct | null | undefined,
): boolean {
  return Boolean(product?.introPrice);
}

function buildPaidFirstPlanQuote(
  annual:
    | { priceString?: string; price?: number; currencyCode?: string }
    | null
    | undefined,
  weekly:
    | { priceString?: string; price?: number; currencyCode?: string }
    | null
    | undefined,
  annualReference?:
    | { priceString?: string; price?: number; currencyCode?: string }
    | null,
): NativePaidFirstPlanQuote | null {
  const annualPriceString = annual?.priceString?.trim();
  const weeklyPriceString = weekly?.priceString?.trim();
  const annualPrice = annual?.price;
  const weeklyPrice = weekly?.price;
  if (
    !annualPriceString ||
    !weeklyPriceString ||
    typeof annualPrice !== 'number' ||
    typeof weeklyPrice !== 'number' ||
    annualPrice <= 0 ||
    weeklyPrice <= 0
  ) {
    return null;
  }

  const referencePriceString = annualReference?.priceString?.trim();
  const referencePrice = annualReference?.price;
  const referenceCurrencyMatches =
    !annual?.currencyCode ||
    !annualReference?.currencyCode ||
    annual.currencyCode === annualReference.currencyCode;
  const hasGenuineReferencePrice = Boolean(
    referencePriceString &&
      typeof referencePrice === 'number' &&
      referencePrice > annualPrice &&
      referenceCurrencyMatches,
  );
  const currencyCode = annual?.currencyCode || weekly?.currencyCode || null;

  return {
    annualPriceString: stripZeroCents(annualPriceString),
    annualReferencePriceString: hasGenuineReferencePrice
      ? stripZeroCents(referencePriceString!)
      : null,
    annualPerWeekString: formatQuoteCurrency(annualPrice / 52, currencyCode),
    weeklyPriceString: stripZeroCents(weeklyPriceString),
    weeklyAnnualizedString: formatQuoteCurrency(weeklyPrice * 52, currencyCode),
    weeklyPerDayString: formatQuoteCurrency(weeklyPrice / 7, currencyCode),
    savingsPercent: Math.max(
      0,
      Math.round((1 - annualPrice / (weeklyPrice * 52)) * 100),
    ),
  };
}

function googleBasePlanPrice(
  product: PurchasesStoreProduct | null | undefined,
  expectedBasePlanId?: string,
): { priceString?: string; price?: number; currencyCode?: string } | null {
  const basePlan = getGooglePlayBasePlanOption(product, expectedBasePlanId);
  const price = basePlan?.fullPricePhase?.price;
  if (!basePlan || !price?.formatted || typeof price.amountMicros !== 'number') {
    return null;
  }
  return {
    priceString: price.formatted,
    price: price.amountMicros / 1_000_000,
    currencyCode: price.currencyCode,
  };
}

/**
 * Returns the exact localized prices for the trial-free Annual + Weekly offer.
 * A native paywall must fail closed when either plan, its no-trial base plan,
 * or its storefront price cannot be verified.
 */
export async function getNativePaidFirstPlanQuote(
  userId: string,
): Promise<NativePaidFirstPlanQuote | null> {
  if (!isNativeBillingEnabled()) return null;

  try {
    const configured = await ensureNativeBillingConfigured(userId);
    if (!configured) return null;
    const revenueCat = await loadRevenueCatModule();
    if (!revenueCat) return null;

    const { PRODUCT_CATEGORY, Purchases } = revenueCat;
    if (getNativePlatform() === 'ios') {
      const [annualResult, weeklyResult, referenceAnnualResult] =
        await Promise.all([
          Purchases.getProducts({
            productIdentifiers: IOS_PAID_FIRST_ANNUAL_PRODUCT_IDS,
            type: PRODUCT_CATEGORY.SUBSCRIPTION,
          }),
          Purchases.getProducts({
            productIdentifiers: [IOS_PAID_FIRST_WEEKLY_PRODUCT_ID],
            type: PRODUCT_CATEGORY.SUBSCRIPTION,
          }),
          Purchases.getProducts({
            productIdentifiers: [IOS_PAID_FIRST_REFERENCE_ANNUAL_PRODUCT_ID],
            type: PRODUCT_CATEGORY.SUBSCRIPTION,
          }),
        ]);
      const annualProduct =
        annualResult.products?.find((product) =>
          IOS_PAID_FIRST_ANNUAL_PRODUCT_IDS.includes(product.identifier),
        ) ?? null;
      const weeklyProduct = weeklyResult.products?.[0] ?? null;
      const referenceAnnualProduct =
        referenceAnnualResult.products?.[0] ?? null;
      // "Charged today" is only true for products with NO introductory offer.
      if (
        hasIosIntroductoryOffer(annualProduct) ||
        hasIosIntroductoryOffer(weeklyProduct)
      ) {
        return null;
      }
      const quote = buildPaidFirstPlanQuote(
        annualProduct,
        weeklyProduct,
        referenceAnnualProduct,
      );
      // The challenger does not render until StoreKit proves that its annual
      // price is below the genuine localized $97 annual reference price. This
      // keeps the paywall off while Apple's scheduled $49.99 change is pending.
      return quote?.annualReferencePriceString ? quote : null;
    }

    if (getNativePlatform() === 'android') {
      const [annualResult, weeklyResult, referenceAnnualResult] =
        await Promise.all([
          Purchases.getProducts({
            productIdentifiers: [ANDROID_PAID_FIRST_ANNUAL_PRODUCT_ID],
            type: PRODUCT_CATEGORY.SUBSCRIPTION,
          }),
          Purchases.getProducts({
            productIdentifiers: [ANDROID_PAID_FIRST_WEEKLY_PRODUCT_ID],
            type: PRODUCT_CATEGORY.SUBSCRIPTION,
          }),
          Purchases.getProducts({
            productIdentifiers: [stripPlayBasePlanSuffix(DEFAULT_CURRENT_ANDROID_ANNUAL_PRODUCT_IDS[0])],
            type: PRODUCT_CATEGORY.SUBSCRIPTION,
          }),
        ]);
      const annualProduct = annualResult.products?.find(
        (product) => isPlayStoreProduct(product, ANDROID_PAID_FIRST_ANNUAL_PRODUCT_ID, ANDROID_PAID_FIRST_ANNUAL_BASE_PLAN_ID),
      ) ?? null;
      const weeklyProduct = weeklyResult.products?.find(
        (product) => isPlayStoreProduct(product, ANDROID_PAID_FIRST_WEEKLY_PRODUCT_ID, ANDROID_PAID_FIRST_WEEKLY_BASE_PLAN_ID),
      ) ?? null;
      const referenceAnnualProduct = referenceAnnualResult.products?.[0] ?? null;
      const quote = buildPaidFirstPlanQuote(
        googleBasePlanPrice(
          annualProduct,
          ANDROID_PAID_FIRST_ANNUAL_BASE_PLAN_ID,
        ),
        googleBasePlanPrice(
          weeklyProduct,
          ANDROID_PAID_FIRST_WEEKLY_BASE_PLAN_ID,
        ),
        googleBasePlanPrice(referenceAnnualProduct),
      );
      return quote?.annualReferencePriceString ? quote : null;
    }

    return null;
  } catch (error) {
    console.warn('Paid-first native price lookup failed:', error);
    return null;
  }
}

export interface NativeAnnualSaveOfferQuote {
  annualPriceString: string;
  referenceAnnualPriceString: string;
  productIdentifier: string;
  savingsPercent: number;
}

/** Only the eligible $97 annual and $12 monthly products for this store qualify. */
export function isNativeAnnualSaveOfferSourceProductId(
  productIdentifier: string | null | undefined,
): boolean {
  if (!productIdentifier) return false;
  const sourceIds = getNativePlatform() === 'android'
    ? [
        ...DEFAULT_CURRENT_ANDROID_ANNUAL_PRODUCT_IDS,
        ...DEFAULT_CURRENT_ANDROID_MONTHLY_PRODUCT_IDS,
      ]
    : [
        ...DEFAULT_CURRENT_IOS_ANNUAL_PRODUCT_IDS,
        IOS_LEGACY_NO_TRIAL_ANNUAL_PRODUCT_ID,
        ...DEFAULT_CURRENT_IOS_MONTHLY_PRODUCT_IDS,
      ];
  return matchesKnownProductIdExact(productIdentifier, sourceIds);
}

export function isNativeAnnualSaveOfferTargetProductId(
  productIdentifier: string | null | undefined,
): boolean {
  if (!productIdentifier) return false;
  const targetIds = getNativePlatform() === 'android'
    ? [
        ANDROID_PAID_FIRST_ANNUAL_PRODUCT_ID,
        `${ANDROID_PAID_FIRST_ANNUAL_PRODUCT_ID}:${ANDROID_PAID_FIRST_ANNUAL_BASE_PLAN_ID}`,
      ]
    : IOS_PAID_FIRST_ANNUAL_PRODUCT_IDS;
  return matchesKnownProductIdExact(productIdentifier, targetIds);
}

export function getNativeAnnualSaveOfferProductId(): string {
  return getNativePlatform() === 'android'
    ? ANDROID_PAID_FIRST_ANNUAL_PRODUCT_ID
    : NATIVE_ANNUAL_SAVE_OFFER_PRODUCT_ID;
}

/**
 * Verified recurring $49.99/year cancellation rescue for an existing native
 * subscriber. The target is the same dedicated no-trial annual product used
 * by paid-first acquisition, while the standard annual remains the genuine
 * localized reference price. Existing subscribers are never moved unless
 * they explicitly accept this offer inside the cancellation flow.
 */
export async function getNativeAnnualSaveOfferQuote(
  userId: string,
): Promise<NativeAnnualSaveOfferQuote | null> {
  if (!isNativeBillingEnabled()) return null;
  const platform = getNativePlatform();
  if (platform !== 'ios' && platform !== 'android') return null;
  const configured = await ensureNativeBillingConfigured(userId);
  if (!configured) return null;
  const revenueCat = await loadRevenueCatModule();
  if (!revenueCat) return null;

  const { PRODUCT_CATEGORY, Purchases } = revenueCat;
  const targetProductIds = platform === 'android'
    ? [ANDROID_PAID_FIRST_ANNUAL_PRODUCT_ID]
    : IOS_PAID_FIRST_ANNUAL_PRODUCT_IDS;
  const referenceProductId = platform === 'android'
    ? stripPlayBasePlanSuffix(DEFAULT_CURRENT_ANDROID_ANNUAL_PRODUCT_IDS[0])
    : IOS_PAID_FIRST_REFERENCE_ANNUAL_PRODUCT_ID;
  const [targetResult, referenceResult] = await Promise.all([
    Purchases.getProducts({
      productIdentifiers: targetProductIds,
      type: PRODUCT_CATEGORY.SUBSCRIPTION,
    }),
    Purchases.getProducts({
      productIdentifiers: [referenceProductId],
      type: PRODUCT_CATEGORY.SUBSCRIPTION,
    }),
  ]);
  const target =
    targetResult.products?.find((product) =>
      platform === 'android'
        ? isPlayStoreProduct(product, ANDROID_PAID_FIRST_ANNUAL_PRODUCT_ID, ANDROID_PAID_FIRST_ANNUAL_BASE_PLAN_ID)
        : targetProductIds.includes(product.identifier),
    ) ?? null;
  const reference = referenceResult.products?.[0] ?? null;
  if (!target || (platform === 'ios' && hasIosIntroductoryOffer(target))) return null;

  const targetPriceSource = platform === 'android'
    ? googleBasePlanPrice(target, ANDROID_PAID_FIRST_ANNUAL_BASE_PLAN_ID)
    : target;
  const referencePriceSource = platform === 'android'
    ? googleBasePlanPrice(reference)
    : reference;

  const targetPrice = targetPriceSource?.price;
  const referencePrice = referencePriceSource?.price;
  const currencyMatches =
    !targetPriceSource?.currencyCode ||
    !referencePriceSource?.currencyCode ||
    targetPriceSource.currencyCode === referencePriceSource.currencyCode;
  if (
    typeof targetPrice !== 'number' ||
    typeof referencePrice !== 'number' ||
    targetPrice <= 0 ||
    referencePrice <= targetPrice ||
    !currencyMatches ||
    !targetPriceSource?.priceString?.trim() ||
    !referencePriceSource?.priceString?.trim()
  ) {
    return null;
  }

  return {
    annualPriceString: stripZeroCents(targetPriceSource.priceString.trim()),
    referenceAnnualPriceString: stripZeroCents(referencePriceSource.priceString.trim()),
    productIdentifier: platform === 'android'
      ? stripPlayBasePlanSuffix(target.identifier)
      : target.identifier,
    savingsPercent: Math.max(
      0,
      Math.round((1 - targetPrice / referencePrice) * 100),
    ),
  };
}

export async function purchaseNativeAnnualSaveOffer(
  userId: string,
  currentProductIdentifier: string,
): Promise<CustomerInfo> {
  const platform = getNativePlatform();
  if (!isNativeAnnualSaveOfferSourceProductId(currentProductIdentifier)) {
    throw new Error(
      'The yearly save offer is only available for an eligible app store subscription.',
    );
  }
  const quote = await getNativeAnnualSaveOfferQuote(userId);
  if (!quote) throw new Error('This yearly save offer is not available right now.');
  if (platform === 'android') {
    const configured = await ensureNativeBillingConfigured(userId);
    if (!configured) throw new NativePurchaseConfigurationError();
    const revenueCat = await loadRevenueCatModule();
    if (!revenueCat) throw new NativePurchaseConfigurationError();
    const { PRODUCT_CATEGORY, PRORATION_MODE, Purchases } = revenueCat;
    await assertRevenueCatIdentity(Purchases, userId);
    await syncRevenueCatAttributionIdentifiers(userId, Purchases, { force: true });
    const { canMakePayments } = await Purchases.canMakePayments();
    if (!canMakePayments) {
      throw new Error('In-app purchases are unavailable on this device.');
    }
    const { products } = await Purchases.getProducts({
      productIdentifiers: [ANDROID_PAID_FIRST_ANNUAL_PRODUCT_ID],
      type: PRODUCT_CATEGORY.SUBSCRIPTION,
    });
    const target = products?.find(
      (product) => isPlayStoreProduct(product, ANDROID_PAID_FIRST_ANNUAL_PRODUCT_ID, ANDROID_PAID_FIRST_ANNUAL_BASE_PLAN_ID),
    ) ?? null;
    const basePlan = getGooglePlayBasePlanOption(
      target,
      ANDROID_PAID_FIRST_ANNUAL_BASE_PLAN_ID,
    );
    const deferred = PRORATION_MODE?.DEFERRED;
    if (!basePlan || typeof deferred !== 'number') {
      throw new NativePurchaseConfigurationError();
    }
    const result = await Purchases.purchaseSubscriptionOption({
      subscriptionOption: basePlan,
      googleProductChangeInfo: {
        oldProductIdentifier: stripPlayBasePlanSuffix(currentProductIdentifier),
        prorationMode: deferred,
      },
    });
    return result.customerInfo;
  }
  return purchaseNativeSubscription(userId, 'annual', {
    nativeOfferMode: 'paid_first_no_trial',
    iosOfferMode: 'paid_first_no_trial',
  });
}

/**
 * Monthly cancel offer (Alex, Oct 21 2026): a $12 monthly subscriber who picks
 * "too expensive" gets 3 months at the store's offer price (US $6.99), then
 * the regular monthly price, on the same plan.
 * - iOS: Apple promotional offer monthly_save_699x3 on the monthly product.
 * - Android (Oct 23 2026): Play developer-determined offer monthly-save-699x3
 *   on base plan 92624, tagged rc-ignore-offer so no other checkout picks it.
 *   Play does not check eligibility for it; the once-ever ledger row does.
 */
export const NATIVE_MONTHLY_699X3_OFFER_KEY = 'monthly_699x3';
export const IOS_MONTHLY_699X3_PRODUCT_ID = 'pevr2wv0gtxs.p3gfapng2app.92624.monthly';
export const IOS_MONTHLY_699X3_APPLE_OFFER_ID = 'monthly_save_699x3';
export const ANDROID_MONTHLY_699X3_PRODUCT_ID = ANDROID_ONBOARDING_MONTHLY_PRODUCT_ID;
export const ANDROID_MONTHLY_699X3_BASE_PLAN_ID = ANDROID_ONBOARDING_MONTHLY_BASE_PLAN_ID;
export const ANDROID_MONTHLY_699X3_PLAY_OFFER_ID = 'monthly-save-699x3';
const ANDROID_MONTHLY_699X3_SOURCE_PRODUCT_ID =
  `${ANDROID_MONTHLY_699X3_PRODUCT_ID}:${ANDROID_MONTHLY_699X3_BASE_PLAN_ID}`;
const MONTHLY_699X3_PERIODS = 3;

export interface NativeMonthly699SaveOfferQuote {
  offerPriceString: string;
  regularPriceString: string;
  productIdentifier: string;
  offerIdentifier: string;
  periods: number;
}

/**
 * The exact $12 monthly plan of this store: the iOS product id, or the Play
 * product:basePlan id on Android. Always false on web.
 */
export function isNativeMonthly699SourceProductId(
  productIdentifier: string | null | undefined,
): boolean {
  if (!productIdentifier) return false;
  const platform = getNativePlatform();
  if (platform === 'ios') {
    return productIdentifier.trim() === IOS_MONTHLY_699X3_PRODUCT_ID;
  }
  if (platform === 'android') {
    return productIdentifier.trim() === ANDROID_MONTHLY_699X3_SOURCE_PRODUCT_ID;
  }
  return false;
}

function phaseIso(phase: { billingPeriod?: { iso8601?: unknown } } | null | undefined): string {
  const iso = phase?.billingPeriod?.iso8601;
  return typeof iso === 'string' ? iso.toUpperCase() : '';
}

/**
 * The Play offer option on the monthly base plan, only if it is exactly
 * 3 monthly payments below a monthly base price in one currency, with no
 * free phase. Null otherwise (fail closed).
 */
function findPlayMonthly699Option(
  products: PurchasesStoreProduct[] | null | undefined,
): SubscriptionOption | null {
  const expectedId = `${ANDROID_MONTHLY_699X3_BASE_PLAN_ID}:${ANDROID_MONTHLY_699X3_PLAY_OFFER_ID}`;
  for (const product of products ?? []) {
    const options = [
      ...(Array.isArray(product?.subscriptionOptions) ? product.subscriptionOptions : []),
      product?.defaultOption,
    ].filter(Boolean) as SubscriptionOption[];
    const option = options.find((candidate) => candidate?.id === expectedId);
    if (!option) continue;
    const intro = option.introPhase;
    const full = option.fullPricePhase;
    const introMicros = Number(intro?.price?.amountMicros);
    const fullMicros = Number(full?.price?.amountMicros);
    if (
      option.isBasePlan !== false ||
      option.freePhase ||
      option.productId !== ANDROID_MONTHLY_699X3_PRODUCT_ID ||
      !intro ||
      !full ||
      phaseIso(intro) !== 'P1M' ||
      phaseIso(full) !== 'P1M' ||
      intro.billingCycleCount !== MONTHLY_699X3_PERIODS ||
      !Number.isFinite(introMicros) ||
      !Number.isFinite(fullMicros) ||
      introMicros <= 0 ||
      introMicros >= fullMicros ||
      !intro.price?.currencyCode ||
      intro.price.currencyCode !== full.price?.currencyCode ||
      !String(intro.price?.formatted ?? '').trim() ||
      !String(full.price?.formatted ?? '').trim()
    ) {
      return null;
    }
    return option;
  }
  return null;
}

function findMonthly699Discount(
  product: PurchasesStoreProduct | null,
): PurchasesStoreProductDiscount | null {
  const discounts: PurchasesStoreProductDiscount[] = Array.isArray(product?.discounts)
    ? product.discounts
    : [];
  return (
    discounts.find(
      (discount) => discount?.identifier === IOS_MONTHLY_699X3_APPLE_OFFER_ID,
    ) ?? null
  );
}

/**
 * Store-verified quote for the monthly offer. Fails closed (null) unless the
 * App Store returns the exact offer: 3 monthly periods below the regular
 * monthly price, with both prices formatted by the store.
 */
export async function getNativeMonthly699SaveOfferQuote(
  userId: string,
): Promise<NativeMonthly699SaveOfferQuote | null> {
  if (!isNativeBillingEnabled()) return null;
  const platform = getNativePlatform();
  if (platform === 'android') return getPlayMonthly699SaveOfferQuote(userId);
  if (platform !== 'ios') return null;
  try {
    const configured = await ensureNativeBillingConfigured(userId);
    if (!configured) return null;
    const revenueCat = await loadRevenueCatModule();
    if (!revenueCat?.Purchases.getPromotionalOffer) return null;
    const { PRODUCT_CATEGORY, Purchases } = revenueCat;
    const { products } = await Purchases.getProducts({
      productIdentifiers: [IOS_MONTHLY_699X3_PRODUCT_ID],
      type: PRODUCT_CATEGORY.SUBSCRIPTION,
    });
    const product =
      products?.find((item) => item.identifier === IOS_MONTHLY_699X3_PRODUCT_ID) ?? null;
    const discount = findMonthly699Discount(product);
    if (!product || !discount) return null;
    const regularPrice = product.price;
    const offerPrice = discount.price;
    if (
      typeof regularPrice !== 'number' ||
      typeof offerPrice !== 'number' ||
      offerPrice <= 0 ||
      offerPrice >= regularPrice ||
      discount.cycles !== MONTHLY_699X3_PERIODS ||
      String(discount.periodUnit ?? '').toUpperCase() !== 'MONTH' ||
      discount.periodNumberOfUnits !== 1 ||
      !String(product.priceString ?? '').trim() ||
      !String(discount.priceString ?? '').trim()
    ) {
      return null;
    }
    return {
      offerPriceString: stripZeroCents(String(discount.priceString).trim()),
      regularPriceString: stripZeroCents(String(product.priceString).trim()),
      productIdentifier: product.identifier,
      offerIdentifier: discount.identifier,
      periods: MONTHLY_699X3_PERIODS,
    };
  } catch (error) {
    console.warn('Monthly save offer lookup failed:', error);
    return null;
  }
}

async function getPlayMonthly699SaveOfferQuote(
  userId: string,
): Promise<NativeMonthly699SaveOfferQuote | null> {
  try {
    const configured = await ensureNativeBillingConfigured(userId);
    if (!configured) return null;
    const revenueCat = await loadRevenueCatModule();
    if (!revenueCat) return null;
    const { PRODUCT_CATEGORY, Purchases } = revenueCat;
    const { products } = await Purchases.getProducts({
      productIdentifiers: [ANDROID_MONTHLY_699X3_PRODUCT_ID],
      type: PRODUCT_CATEGORY.SUBSCRIPTION,
    });
    const option = findPlayMonthly699Option(products);
    if (!option) return null;
    return {
      offerPriceString: stripZeroCents(String(option.introPhase.price.formatted).trim()),
      regularPriceString: stripZeroCents(String(option.fullPricePhase.price.formatted).trim()),
      productIdentifier: ANDROID_MONTHLY_699X3_SOURCE_PRODUCT_ID,
      offerIdentifier: ANDROID_MONTHLY_699X3_PLAY_OFFER_ID,
      periods: MONTHLY_699X3_PERIODS,
    };
  } catch (error) {
    console.warn('Monthly save offer lookup failed:', error);
    return null;
  }
}

/**
 * Android: move the current monthly subscription onto the Play offer on the
 * same base plan. IMMEDIATE_WITHOUT_PRORATION is Play's "charge at the next
 * billing date": nothing is charged now, the paid (or trial) time is kept,
 * and the offer price starts on the next renewal.
 */
async function purchasePlayMonthly699SaveOffer(
  userId: string,
  currentProductIdentifier: string,
): Promise<CustomerInfo> {
  if (!isNativeMonthly699SourceProductId(currentProductIdentifier)) {
    throw new Error('This offer is only available for an active monthly Google Play subscription.');
  }
  const configured = await ensureNativeBillingConfigured(userId);
  if (!configured) throw new NativePurchaseConfigurationError();
  const revenueCat = await loadRevenueCatModule();
  if (!revenueCat) throw new NativePurchaseConfigurationError();
  const { PRODUCT_CATEGORY, PRORATION_MODE, Purchases } = revenueCat;
  await assertRevenueCatIdentity(Purchases, userId);
  const { canMakePayments } = await Purchases.canMakePayments();
  if (!canMakePayments) {
    throw new Error('In-app purchases are unavailable on this device.');
  }
  const { products } = await Purchases.getProducts({
    productIdentifiers: [ANDROID_MONTHLY_699X3_PRODUCT_ID],
    type: PRODUCT_CATEGORY.SUBSCRIPTION,
  });
  const option = findPlayMonthly699Option(products);
  const withoutProration = PRORATION_MODE?.IMMEDIATE_WITHOUT_PRORATION;
  if (!option || typeof withoutProration !== 'number') {
    throw new NativePurchaseConfigurationError();
  }
  const result = await Purchases.purchaseSubscriptionOption({
    subscriptionOption: option,
    googleProductChangeInfo: {
      oldProductIdentifier: stripPlayBasePlanSuffix(currentProductIdentifier.trim()),
      prorationMode: withoutProration,
    },
  });
  return result.customerInfo;
}

/**
 * Redeem the monthly offer on the subscriber's current monthly plan.
 * iOS: RevenueCat signs monthly_save_699x3 (In-App Purchase Key in its
 * dashboard); Apple refuses it if the account is not eligible.
 * Android: the Play offer on the same base plan (see above).
 */
export async function purchaseNativeMonthly699SaveOffer(
  userId: string,
  currentProductIdentifier: string,
): Promise<CustomerInfo> {
  if (getNativePlatform() === 'android') {
    return purchasePlayMonthly699SaveOffer(userId, currentProductIdentifier);
  }
  if (!isNativeMonthly699SourceProductId(currentProductIdentifier)) {
    throw new Error('This offer is only available for an active monthly App Store subscription.');
  }
  const configured = await ensureNativeBillingConfigured(userId);
  if (!configured) throw new NativePurchaseConfigurationError();
  const revenueCat = await loadRevenueCatModule();
  if (
    !revenueCat?.Purchases.getPromotionalOffer ||
    !revenueCat.Purchases.purchaseDiscountedProduct
  ) {
    throw new NativePurchaseConfigurationError();
  }
  const { PRODUCT_CATEGORY, Purchases } = revenueCat;
  await assertRevenueCatIdentity(Purchases, userId);
  const { canMakePayments } = await Purchases.canMakePayments();
  if (!canMakePayments) {
    throw new Error('In-app purchases are unavailable on this device.');
  }
  const { products } = await Purchases.getProducts({
    productIdentifiers: [IOS_MONTHLY_699X3_PRODUCT_ID],
    type: PRODUCT_CATEGORY.SUBSCRIPTION,
  });
  const product =
    products?.find((item) => item.identifier === IOS_MONTHLY_699X3_PRODUCT_ID) ?? null;
  const discount = findMonthly699Discount(product);
  if (!product || !discount) throw new NativePurchaseConfigurationError();
  // Capacitor plugin methods are called on the plugin object, never detached.
  const promotionalOffer = await Purchases.getPromotionalOffer!({ product, discount });
  if (!promotionalOffer) {
    throw new Error('This offer is not available on your account right now.');
  }
  const result = await Purchases.purchaseDiscountedProduct!({
    product,
    discount: promotionalOffer,
  });
  return result.customerInfo;
}

export async function purchaseNativeSubscription(
  userId: string,
  tier: NativeBillingTier,
  options: NativeSubscriptionPurchaseOptions = {},
): Promise<CustomerInfo> {
  const configured = await ensureNativeBillingConfigured(userId);
  if (!configured) {
    throw new Error('Native in-app billing is not configured yet.');
  }

  const revenueCat = await loadRevenueCatModule();
  if (!revenueCat) {
    throw new Error('Native in-app billing is unavailable in this environment.');
  }

  const { PACKAGE_TYPE, Purchases } = revenueCat;
  await assertRevenueCatIdentity(Purchases, userId);
  await syncRevenueCatAttributionIdentifiers(userId, Purchases, { force: true });

  const { canMakePayments } = await Purchases.canMakePayments();
  if (!canMakePayments) {
    throw new Error('In-app purchases are unavailable on this device.');
  }

  if (
    getNativePlatform() === 'ios' &&
    (options.iosOfferMode === 'no_trial' ||
      options.iosOfferMode === 'paid_first_no_trial')
  ) {
    return purchaseNativeSubscriptionFromDirectProducts(tier, userId, options);
  }
  if (
    getNativePlatform() === 'android' &&
    options.nativeOfferMode === 'paid_first_no_trial'
  ) {
    return purchaseNativeSubscriptionFromDirectProducts(tier, userId, options);
  }

  try {
    const offerings = await Purchases.getOfferings();
    const offering = offerings.current;
    if (!offering) {
      throw new Error('No active offering is configured in RevenueCat.');
    }

    const targetPackage = getPackageForTier(offering, tier, PACKAGE_TYPE);
    if (!targetPackage) {
      throw new Error(`RevenueCat offering is missing a ${tier} package.`);
    }

    if (
      getNativePlatform() === 'android' &&
      options.googleOfferMode === 'base_plan'
    ) {
      const basePlan = getGooglePlayBasePlanOption(targetPackage.product);
      if (!basePlan) {
        throw new NativePurchaseConfigurationError(
          `RevenueCat offering is missing a trial-free Google Play ${tier} base plan.`,
        );
      }
      const result = await Purchases.purchaseSubscriptionOption({
        subscriptionOption: basePlan,
      });
      return result.customerInfo;
    }

    const result = await Purchases.purchasePackage({ aPackage: targetPackage });
    return result.customerInfo;
  } catch (error) {
    if (!isOfferingsConfigurationError(error)) {
      throw error;
    }

    console.warn(
      'RevenueCat offerings are misconfigured for this app. Falling back to direct store products.',
      error
    );
    return purchaseNativeSubscriptionFromDirectProducts(tier, userId, options);
  }
}

function mapRevenueCatIntroEligibilityStatus(status: number | undefined): NativeIntroOfferEligibility {
  switch (status) {
    case 2:
      return 'eligible';
    case 1:
      return 'ineligible';
    case 3:
      return 'no_offer';
    default:
      return 'unknown';
  }
}

function getGooglePlayFreePhaseDays(
  product: PurchasesStoreProduct | null | undefined
): number[] {
  const options = [
    product?.defaultOption,
    ...(Array.isArray(product?.subscriptionOptions)
      ? product.subscriptionOptions
      : []),
  ].filter(Boolean);
  const seen = new Set<unknown>();
  const days: number[] = [];

  for (const option of options) {
    if (seen.has(option)) continue;
    seen.add(option);
    const period = option?.freePhase?.billingPeriod;
    const iso =
      typeof period?.iso8601 === 'string' ? period.iso8601.toUpperCase() : '';
    const isoMatch = /^P(\d+)([DWMY])$/.exec(iso);
    if (isoMatch) {
      const amount = Number(isoMatch[1]);
      const unit = isoMatch[2];
      if (unit === 'D') days.push(amount);
      if (unit === 'W') days.push(amount * 7);
      continue;
    }

    const amount = Number(period?.value);
    const unit = String(period?.unit || '').toLowerCase();
    if (!Number.isFinite(amount) || amount < 1) continue;
    if (unit === 'day') days.push(amount);
    if (unit === 'week') days.push(amount * 7);
  }

  return days;
}

export function getIosFreeIntroDays(
  product: PurchasesStoreProduct | null | undefined
): number[] {
  const introPrice = product?.introPrice;
  if (!introPrice || Number(introPrice.price) !== 0) return [];

  const cycles = Math.max(1, Number(introPrice.cycles) || 1);
  const period = String(introPrice.period || '').toUpperCase();
  const isoMatch = /^P(\d+)([DW])$/.exec(period);
  if (isoMatch) {
    const amount = Number(isoMatch[1]) * cycles;
    return [isoMatch[2] === 'W' ? amount * 7 : amount];
  }

  const amount = Number(introPrice.periodNumberOfUnits) * cycles;
  const unit = String(introPrice.periodUnit || '').toLowerCase();
  if (!Number.isFinite(amount) || amount < 1) return [];
  if (unit === 'day') return [amount];
  if (unit === 'week') return [amount * 7];
  return [];
}

export async function getNativeIntroOfferEligibility(
  userId: string,
  tier: NativeBillingTier
): Promise<NativeIntroOfferEligibility> {
  if (!isNativeBillingEnabled()) return 'unsupported';
  const platform = getNativePlatform();
  if (platform !== 'ios' && platform !== 'android') return 'unsupported';

  const configured = await ensureNativeBillingConfigured(userId);
  if (!configured) return 'unknown';

  const revenueCat = await loadRevenueCatModule();
  if (!revenueCat) return 'unknown';

  const { PACKAGE_TYPE, Purchases } = revenueCat;
  const offerings = await Purchases.getOfferings();
  const offering = offerings.current;
  if (!offering) return 'unknown';

  const targetPackage = getPackageForTier(offering, tier, PACKAGE_TYPE);
  const productIdentifier = targetPackage?.product?.identifier;
  if (!productIdentifier) return 'unknown';

  if (platform === 'android') {
    const expectedDays = tier === 'annual' ? 14 : 7;
    return getGooglePlayFreePhaseDays(targetPackage.product).includes(
      expectedDays
    )
      ? 'eligible'
      : 'no_offer';
  }

  const expectedDays = tier === 'annual' ? 14 : 7;
  if (!getIosFreeIntroDays(targetPackage.product).includes(expectedDays)) {
    return 'no_offer';
  }

  const eligibilityMap =
    await Purchases.checkTrialOrIntroductoryPriceEligibility({
      productIdentifiers: [productIdentifier],
    });

  return mapRevenueCatIntroEligibilityStatus(eligibilityMap[productIdentifier]?.status);
}

// ── Onboarding v3 bridge quote ──
// Ported from 9931f6d9:src/services/nativeBilling.ts (getNativeBridgeQuote and its types).

export const NATIVE_BRIDGE_ANNUAL_TRIAL_DAYS = 14;
export const NATIVE_BRIDGE_MONTHLY_TRIAL_DAYS = 7;
/** The only products the bridge quote may describe (store ids, Play base plan suffix stripped). */
export const NATIVE_BRIDGE_ANNUAL_PRODUCT_ID = 'pevr2wv0gtxs.p3gfapng2app.93103.month';
export const NATIVE_BRIDGE_MONTHLY_PRODUCT_ID = 'pevr2wv0gtxs.p3gfapng2app.92624.monthly';

/** True when the offering resolved to the exact bridge products purchaseNativeSubscription buys. */
export function isNativeBridgeProductPair(
  annualIdentifier: string | null | undefined,
  monthlyIdentifier: string | null | undefined,
): boolean {
  if (!annualIdentifier || !monthlyIdentifier) return false;
  return (
    stripPlayBasePlanSuffix(annualIdentifier) === NATIVE_BRIDGE_ANNUAL_PRODUCT_ID &&
    stripPlayBasePlanSuffix(monthlyIdentifier) === NATIVE_BRIDGE_MONTHLY_PRODUCT_ID
  );
}

/** Exact store facts for the onboarding bridge offer (Annual 14-day trial + Monthly 7-day trial). */
export interface NativeBridgeQuote {
  annualPriceString: string;
  annualPerMonthString: string | null;
  /** Null only when StoreKit reports this account already used the intro offer. */
  annualTrialDays: typeof NATIVE_BRIDGE_ANNUAL_TRIAL_DAYS | null;
  monthlyPriceString: string;
  monthlyTrialDays: typeof NATIVE_BRIDGE_MONTHLY_TRIAL_DAYS | null;
  savingsPercent: number;
  /** 12x monthly minus annual, whole units in the storefront currency ("$47"); null = hide. */
  annualSavingsString?: string | null;
  /** 12x the store monthly price in the storefront currency, for the honest annual comparison. */
  monthlyAnnualizedString?: string | null;
  /** Storefront currency, so a free trial reads as zero in the buyer's currency. */
  currencyCode: string | null;
}

function googleDefaultOptionTrialPrice(
  product: PurchasesStoreProduct | null | undefined,
  expectedTrialDays: number,
): { priceString?: string; price?: number; currencyCode?: string; trialDays: number | null } | null {
  // purchasePackage buys the default option, so the quote must describe it.
  // Google Play omits offers the account already redeemed, so a default
  // option with no free phase is the verified paid-today base plan. Any other
  // free period than the advertised one fails closed.
  const option = product?.defaultOption;
  if (!option) return null;
  const freeDays = getGooglePlayFreePhaseDays({ defaultOption: option } as PurchasesStoreProduct);
  const trialDays = freeDays.length === 0
    ? null
    : freeDays.includes(expectedTrialDays)
      ? expectedTrialDays
      : undefined;
  if (trialDays === undefined) return null;
  const price = option.fullPricePhase?.price;
  if (!price?.formatted || typeof price.amountMicros !== 'number') return null;
  return {
    priceString: price.formatted,
    price: price.amountMicros / 1_000_000,
    currencyCode: price.currencyCode,
    trialDays,
  };
}

/**
 * Reads the same current-offering packages that purchaseNativeSubscription
 * buys and returns their localized prices only when both carry the bridge's
 * exact free periods. Any missing offering, package, price or trial returns
 * null so the paywall fails closed instead of printing compiled prices.
 * Google Play omits offers the account cannot redeem; a default option with
 * no free phase is quoted as the paid-today base plan (trial days null).
 */
export async function getNativeBridgeQuote(
  userId: string,
): Promise<NativeBridgeQuote | null> {
  if (!isNativeBillingEnabled()) return null;
  const platform = getNativePlatform();
  if (platform !== 'ios' && platform !== 'android') return null;

  try {
    const configured = await ensureNativeBillingConfigured(userId);
    if (!configured) return null;
    const revenueCat = await loadRevenueCatModule();
    if (!revenueCat) return null;
    const { PACKAGE_TYPE, Purchases } = revenueCat;
    const offerings = await Purchases.getOfferings();
    const offering = offerings.current;
    if (!offering) return null;
    const annualProduct = getPackageForTier(offering, 'annual', PACKAGE_TYPE)?.product ?? null;
    const monthlyProduct = getPackageForTier(offering, 'monthly', PACKAGE_TYPE)?.product ?? null;
    if (!annualProduct?.identifier || !monthlyProduct?.identifier) return null;
    // Fail closed when the current offering is not the bridge catalog
    // (offering `default`): the paywall must describe exactly what it sells.
    if (!isNativeBridgeProductPair(annualProduct.identifier, monthlyProduct.identifier)) return null;

    let annualPrice: { priceString?: string; price?: number; currencyCode?: string } | null;
    let monthlyPrice: { priceString?: string; price?: number; currencyCode?: string } | null;
    let annualTrialDays: NativeBridgeQuote['annualTrialDays'] = NATIVE_BRIDGE_ANNUAL_TRIAL_DAYS;
    let monthlyTrialDays: NativeBridgeQuote['monthlyTrialDays'] = NATIVE_BRIDGE_MONTHLY_TRIAL_DAYS;

    if (platform === 'android') {
      const annualOption = googleDefaultOptionTrialPrice(annualProduct, NATIVE_BRIDGE_ANNUAL_TRIAL_DAYS);
      const monthlyOption = googleDefaultOptionTrialPrice(monthlyProduct, NATIVE_BRIDGE_MONTHLY_TRIAL_DAYS);
      if (!annualOption || !monthlyOption) return null;
      annualPrice = annualOption;
      monthlyPrice = monthlyOption;
      annualTrialDays = annualOption.trialDays === null ? null : NATIVE_BRIDGE_ANNUAL_TRIAL_DAYS;
      monthlyTrialDays = monthlyOption.trialDays === null ? null : NATIVE_BRIDGE_MONTHLY_TRIAL_DAYS;
    } else {
      if (
        !getIosFreeIntroDays(annualProduct).includes(NATIVE_BRIDGE_ANNUAL_TRIAL_DAYS) ||
        !getIosFreeIntroDays(monthlyProduct).includes(NATIVE_BRIDGE_MONTHLY_TRIAL_DAYS)
      ) {
        return null;
      }
      annualPrice = annualProduct;
      monthlyPrice = monthlyProduct;
      const eligibilityMap = await Purchases.checkTrialOrIntroductoryPriceEligibility({
        productIdentifiers: [annualProduct.identifier, monthlyProduct.identifier],
      });
      // Unknown stays a trial: StoreKit confirms it on the purchase sheet.
      if (mapRevenueCatIntroEligibilityStatus(eligibilityMap[annualProduct.identifier]?.status) === 'ineligible') {
        annualTrialDays = null;
      }
      if (mapRevenueCatIntroEligibilityStatus(eligibilityMap[monthlyProduct.identifier]?.status) === 'ineligible') {
        monthlyTrialDays = null;
      }
    }

    const quote = buildAnnualTrialMonthlyQuote(annualPrice, monthlyPrice, null);
    if (!quote) return null;
    return {
      annualPriceString: quote.annualPriceString,
      annualPerMonthString: quote.annualPerMonthString,
      annualTrialDays,
      monthlyPriceString: quote.monthlyPriceString,
      monthlyTrialDays,
      savingsPercent: quote.savingsPercent,
      annualSavingsString: quote.annualSavingsString ?? null,
      monthlyAnnualizedString:
        typeof monthlyPrice?.price === 'number'
          ? (() => {
              const s = formatQuoteCurrency(
                monthlyPrice.price * 12,
                monthlyPrice.currencyCode ?? annualPrice?.currencyCode ?? null,
              );
              return s ? stripZeroCents(s) : null;
            })()
          : null,
      currencyCode: annualPrice?.currencyCode ?? monthlyPrice?.currencyCode ?? null,
    };
  } catch (error) {
    console.warn('Bridge native price lookup failed:', error);
    return null;
  }
}


// ── Annual-led offering: annual 7-day trial lead + weekly no-trial anchor ──
//
// iOS sells annual through the DEDICATED product below. Never point this at
// jp_premium_annual_no_trial or the legacy 14-day annual: Apple auto-applies
// a product's introductory offer to every eligible purchase, so sharing a
// product with another paywall mode makes that mode's copy false.
// Play reuses the legacy annual base plan but the client must select the
// trial-7d option EXPLICITLY: automatic package purchase picks the longest
// eligible trial, which would silently grant 14 days.

const DEFAULT_ANNUAL_LED_OFFERING_ID = 'annual-led';
const IOS_ANNUAL_LED_ANNUAL_PRODUCT_ID =
  IOS_PAID_FIRST_RESCUE_ANNUAL_PRODUCT_ID;
const PLAY_ANNUAL_LED_TRIAL_OFFER_ID = 'trial-7d';
export const ANNUAL_LED_TRIAL_DAYS = 7;

function getAnnualLedOfferingId(): string {
  return (
    env.revenueCatAnnualLedOfferingId?.trim() || DEFAULT_ANNUAL_LED_OFFERING_ID
  );
}

export interface AnnualLedQuote {
  annualPriceString: string;
  /** Localized annual price divided into a per-week figure; null = hide. */
  annualPerWeekString: string | null;
  /** Verified free-trial days on the annual plan; null = no trial claim. */
  annualTrialDays: number | null;
  weeklyPriceString: string;
  /** Localized weekly price multiplied by 52; null = hide comparison. */
  weeklyAnnualizedString: string | null;
  /**
   * Localized weekly price divided into a per-day figure; null = hide.
   * The weekly card sells "about $X a day" (Alex, Aug 13): approachable on
   * its own terms while the annual card carries the savings comparison.
   */
  weeklyPerDayString: string | null;
  savingsPercent: number;
  rescueEligible: boolean;
}

/**
 * Cosmetic display trim: drop zero cents from a localized price string
 * ("$97.00" renders as "$97", "97,00 €" as "97 €"). The amount is unchanged
 * and non-zero cents are always kept, so the store price stays the truth.
 */
function stripZeroCents(label: string): string {
  return label.replace(/(\d)[.,]00(?!\d)/, '$1');
}

function formatQuoteCurrency(
  amount: number,
  currencyCode: string | null | undefined,
): string | null {
  if (!Number.isFinite(amount) || amount <= 0 || !currencyCode) return null;
  try {
    return stripZeroCents(
      new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
      }).format(amount),
    );
  } catch {
    return null;
  }
}

function buildAnnualTrialMonthlyQuote(
  annual: { priceString?: string; price?: number; currencyCode?: string } | null,
  monthly: { priceString?: string; price?: number; currencyCode?: string } | null,
  annualTrialDays: 7 | null,
): NativeAnnualTrialMonthlyQuote | null {
  const annualPriceString = annual?.priceString?.trim();
  const monthlyPriceString = monthly?.priceString?.trim();
  const annualPrice = annual?.price;
  const monthlyPrice = monthly?.price;
  const currenciesMatch =
    !annual?.currencyCode ||
    !monthly?.currencyCode ||
    annual.currencyCode === monthly.currencyCode;
  if (
    !annualPriceString ||
    !monthlyPriceString ||
    typeof annualPrice !== 'number' ||
    typeof monthlyPrice !== 'number' ||
    annualPrice <= 0 ||
    monthlyPrice <= 0 ||
    !currenciesMatch
  ) {
    return null;
  }

  return {
    annualPriceString: stripZeroCents(annualPriceString),
    annualPerMonthString: formatQuoteCurrency(
      annualPrice / 12,
      annual.currencyCode || monthly.currencyCode || null,
    ),
    annualTrialDays,
    monthlyPriceString: stripZeroCents(monthlyPriceString),
    savingsPercent: Math.max(
      0,
      Math.round((1 - annualPrice / (monthlyPrice * 12)) * 100),
    ),
    annualSavingsString: formatAnnualSavings(
      annualPrice,
      monthlyPrice,
      annual.currencyCode || monthly.currencyCode || null,
    ),
  };
}

/**
 * Dormant, remotely enabled successor to Annual + Weekly. It renders only
 * after StoreKit proves that the dedicated Monthly product exists and has no
 * introductory offer, so the remote switch never requires guessed terms.
 */
export async function getNativeAnnualTrialMonthlyQuote(
  userId: string,
): Promise<NativeAnnualTrialMonthlyQuote | null> {
  if (!isNativeBillingEnabled()) return null;
  const platform = getNativePlatform();
  if (platform !== 'ios' && platform !== 'android') return null;

  try {
    const configured = await ensureNativeBillingConfigured(userId);
    if (!configured) return null;
    const revenueCat = await loadRevenueCatModule();
    if (!revenueCat) return null;
    const { PACKAGE_TYPE, PRODUCT_CATEGORY, Purchases } = revenueCat;
    const annualProductId = platform === 'ios'
      ? IOS_PAID_FIRST_RESCUE_ANNUAL_PRODUCT_ID
      : ANDROID_PAID_FIRST_ANNUAL_PRODUCT_ID;
    const monthlyProductId = platform === 'ios'
      ? IOS_ONBOARDING_MONTHLY_NO_TRIAL_PRODUCT_ID
      : ANDROID_ONBOARDING_MONTHLY_PRODUCT_ID;
    if (platform === 'ios') {
      // Reuse the exact annual package already used by the Annual + Weekly
      // arm. This keeps both remotely selectable layouts on one verified
      // annual source while StoreKit price changes propagate.
      const [annualOffering, monthlyResult] = await Promise.all([
        getAnnualLedOffering(revenueCat),
        Purchases.getProducts({
          productIdentifiers: [monthlyProductId],
          type: PRODUCT_CATEGORY.SUBSCRIPTION,
        }),
      ]);
      const annualPackage = annualOffering
        ? getPackageForTier(annualOffering, 'annual', PACKAGE_TYPE)
        : null;
      const annualProduct = annualPackage?.product ?? null;
      const monthlyProduct = monthlyResult.products?.find(
        (product) => product.identifier === monthlyProductId,
      ) ?? null;
      if (
        annualProduct?.identifier !== annualProductId ||
        monthlyProduct?.identifier !== monthlyProductId ||
        !getIosFreeIntroDays(annualProduct).includes(
          PAID_FIRST_RESCUE_TRIAL_DAYS,
        ) ||
        hasIosIntroductoryOffer(monthlyProduct) ||
        !(await isIosAnnualTrialAtApprovedPrice(
          revenueCat,
          annualProduct,
        ))
      ) {
        return null;
      }
      const eligibilityMap =
        await Purchases.checkTrialOrIntroductoryPriceEligibility({
          productIdentifiers: [annualProductId],
        });
      const eligibility = mapRevenueCatIntroEligibilityStatus(
        eligibilityMap[annualProductId]?.status,
      );
      return buildAnnualTrialMonthlyQuote(
        annualProduct,
        monthlyProduct,
        eligibility === 'ineligible' || eligibility === 'no_offer'
          ? null
          : PAID_FIRST_RESCUE_TRIAL_DAYS,
      );
    }

    const [annualResult, monthlyResult] = await Promise.all([
      Purchases.getProducts({
        productIdentifiers: [annualProductId],
        type: PRODUCT_CATEGORY.SUBSCRIPTION,
      }),
      Purchases.getProducts({
        productIdentifiers: [monthlyProductId],
        type: PRODUCT_CATEGORY.SUBSCRIPTION,
      }),
    ]);
    const annualProduct = annualResult.products?.find(
      (product) => product.identifier === annualProductId,
    ) ?? null;
    const monthlyProduct = monthlyResult.products?.find(
      (product) => product.identifier === monthlyProductId,
    ) ?? null;
    if (!annualProduct || !monthlyProduct) return null;

    const annualBasePrice = googleBasePlanPrice(
      annualProduct,
      ANDROID_PAID_FIRST_ANNUAL_BASE_PLAN_ID,
    );
    const monthlyBasePrice = googleBasePlanPrice(
      monthlyProduct,
      ANDROID_ONBOARDING_MONTHLY_BASE_PLAN_ID,
    );
    if (!annualBasePrice || !monthlyBasePrice) return null;
    return buildAnnualTrialMonthlyQuote(
      annualBasePrice,
      monthlyBasePrice,
      findPlayPaidFirstTrialOption(annualProduct)
        ? PAID_FIRST_RESCUE_TRIAL_DAYS
        : null,
    );
  } catch (error) {
    console.warn('Annual-trial monthly native price lookup failed:', error);
    return null;
  }
}

/**
 * Resolve both paid plans directly. Never consult the current offering or
 * trial eligibility: Apple applies an intro automatically, so any intro on
 * either SKU is a configuration failure even for a previously subscribed user.
 */
async function loadNativeAnnualMonthlyPaidPlans(userId: string) {
  const platform = getNativePlatform();
  if (!isNativeBillingEnabled() || (platform !== 'ios' && platform !== 'android')) return null;
  if (!(await ensureNativeBillingConfigured(userId))) return null;
  const revenueCat = await loadRevenueCatModule();
  if (!revenueCat) return null;
  const { Purchases, PRODUCT_CATEGORY } = revenueCat;
  await assertRevenueCatIdentity(Purchases, userId);
  const annualId = platform === 'ios' ? IOS_PAID_FIRST_ANNUAL_PRODUCT_IDS[0] : ANDROID_PAID_FIRST_ANNUAL_PRODUCT_ID;
  const monthlyId = platform === 'ios' ? IOS_ONBOARDING_MONTHLY_NO_TRIAL_PRODUCT_ID : ANDROID_ONBOARDING_MONTHLY_PRODUCT_ID;
  const [annualResult, monthlyResult] = await Promise.all([
    Purchases.getProducts({ productIdentifiers: [annualId], type: PRODUCT_CATEGORY.SUBSCRIPTION }),
    Purchases.getProducts({ productIdentifiers: [monthlyId], type: PRODUCT_CATEGORY.SUBSCRIPTION }),
  ]);
  const matchesPaidProduct = (product: PurchasesStoreProduct, id: string, basePlan: string) =>
    product.identifier === id || (platform === 'android' && product.identifier === `${id}:${basePlan}`);
  const annual = annualResult.products?.find((product) => matchesPaidProduct(product, annualId, ANDROID_PAID_FIRST_ANNUAL_BASE_PLAN_ID));
  const monthly = monthlyResult.products?.find((product) => matchesPaidProduct(product, monthlyId, ANDROID_ONBOARDING_MONTHLY_BASE_PLAN_ID));
  if (!annual || !monthly) return null;

  const annualOption = platform === 'android'
    ? getGooglePlayBasePlanOption(annual, ANDROID_PAID_FIRST_ANNUAL_BASE_PLAN_ID) : null;
  const monthlyOption = platform === 'android'
    ? getGooglePlayBasePlanOption(monthly, ANDROID_ONBOARDING_MONTHLY_BASE_PLAN_ID) : null;
  const validPaidBasePlan = (option: SubscriptionOption, productId: string, basePlan: string, period: string) =>
    option && option.id === basePlan && option.productId === productId &&
    option.storeProductId === `${productId}:${basePlan}` && option.isPrepaid !== true &&
    !option.freePhase && !option.introPhase &&
    option.fullPricePhase?.billingPeriod?.iso8601 === period &&
    (!Array.isArray(option.pricingPhases) || option.pricingPhases.length === 1);
  if (platform === 'ios') {
    if (hasIosIntroductoryOffer(annual) || hasIosIntroductoryOffer(monthly) ||
        annual.subscriptionPeriod !== 'P1Y' || monthly.subscriptionPeriod !== 'P1M') return null;
  } else if (!validPaidBasePlan(annualOption, annualId, ANDROID_PAID_FIRST_ANNUAL_BASE_PLAN_ID, 'P1Y') ||
      !validPaidBasePlan(monthlyOption, monthlyId, ANDROID_ONBOARDING_MONTHLY_BASE_PLAN_ID, 'P1M')) {
    return null;
  }
  const annualPrice = platform === 'ios' ? annual : googleBasePlanPrice(annual, ANDROID_PAID_FIRST_ANNUAL_BASE_PLAN_ID);
  const monthlyPrice = platform === 'ios' ? monthly : googleBasePlanPrice(monthly, ANDROID_ONBOARDING_MONTHLY_BASE_PLAN_ID);
  const currency = annualPrice?.currencyCode;
  if (!currency || currency !== monthlyPrice?.currencyCode ||
      !Number.isFinite(annualPrice?.price) || !Number.isFinite(monthlyPrice?.price)) return null;
  // Every storefront, the US included, shows the exact price the store will
  // charge. No compiled amount gates the offer (VIP/Pro plan, section 6).
  const display = buildAnnualTrialMonthlyQuote(annualPrice, monthlyPrice, null);
  if (!display) return null;
  const quote: NativeAnnualMonthlyPaidQuote = {
    annualPriceString: display.annualPriceString,
    annualPerMonthString: display.annualPerMonthString,
    monthlyPriceString: display.monthlyPriceString,
    annualPrice: annualPrice.price,
    monthlyPrice: monthlyPrice.price,
    currencyCode: currency,
    savingsPercent: display.savingsPercent,
    annualSavingsString: display.annualSavingsString ?? null,
  };
  return { revenueCat, platform, annual, monthly, annualOption, monthlyOption, quote };
}

export async function getNativeAnnualMonthlyPaidQuote(userId: string): Promise<NativeAnnualMonthlyPaidQuote | null> {
  try {
    return (await loadNativeAnnualMonthlyPaidPlans(userId))?.quote ?? null;
  } catch (error) {
    console.warn('Paid annual/monthly price lookup failed:', error);
    return null;
  }
}

export async function purchaseNativeAnnualMonthlyPaidPlan(
  userId: string,
  tier: 'annual' | 'monthly',
  expectedPrice?: { value: number; currency: string },
): Promise<CustomerInfo> {
  const plans = await loadNativeAnnualMonthlyPaidPlans(userId);
  if (!plans || (tier !== 'annual' && tier !== 'monthly')) throw new NativePurchaseConfigurationError();
  const value = tier === 'annual' ? plans.quote.annualPrice : plans.quote.monthlyPrice;
  if (expectedPrice && (value !== expectedPrice.value || plans.quote.currencyCode !== expectedPrice.currency)) {
    throw new Error('Your store price changed. Please reload the membership options and try again.');
  }
  const { Purchases } = plans.revenueCat;
  await syncRevenueCatAttributionIdentifiers(userId, Purchases, { force: true });
  const { canMakePayments } = await Purchases.canMakePayments();
  if (!canMakePayments) throw new Error('In-app purchases are unavailable on this device.');
  const result = plans.platform === 'ios'
    ? await Purchases.purchaseStoreProduct({ product: tier === 'annual' ? plans.annual : plans.monthly })
    : await Purchases.purchaseSubscriptionOption({ subscriptionOption: tier === 'annual' ? plans.annualOption : plans.monthlyOption });
  return result.customerInfo;
}

export async function purchaseNativeAnnualTrialMonthlyPlan(
  userId: string,
  tier: 'annual' | 'monthly',
): Promise<CustomerInfo> {
  if (!(await getNativeAnnualTrialMonthlyQuote(userId))) {
    throw new NativePurchaseConfigurationError();
  }
  const revenueCat = await loadRevenueCatModule();
  if (!revenueCat) throw new NativePurchaseConfigurationError();
  const { PACKAGE_TYPE, PRODUCT_CATEGORY, Purchases } = revenueCat;
  await assertRevenueCatIdentity(Purchases, userId);
  await syncRevenueCatAttributionIdentifiers(userId, Purchases, { force: true });
  const { canMakePayments } = await Purchases.canMakePayments();
  if (!canMakePayments) {
    throw new Error('In-app purchases are unavailable on this device.');
  }

  const platform = getNativePlatform();
  if (platform === 'ios' && tier === 'annual') {
    const offering = await getAnnualLedOffering(revenueCat);
    const annualPackage = offering
      ? getPackageForTier(offering, 'annual', PACKAGE_TYPE)
      : null;
    const annualProduct = annualPackage?.product;
    if (
      !annualPackage ||
      annualProduct?.identifier !== IOS_PAID_FIRST_RESCUE_ANNUAL_PRODUCT_ID ||
      !getIosFreeIntroDays(annualProduct).includes(
        PAID_FIRST_RESCUE_TRIAL_DAYS,
      ) ||
      !(await isIosAnnualTrialAtApprovedPrice(revenueCat, annualProduct))
    ) {
      throw new NativePurchaseConfigurationError();
    }
    const result = await Purchases.purchasePackage({
      aPackage: annualPackage,
    });
    return result.customerInfo;
  }

  const productIdentifier = platform === 'ios'
    ? IOS_ONBOARDING_MONTHLY_NO_TRIAL_PRODUCT_ID
    : tier === 'annual'
      ? ANDROID_PAID_FIRST_ANNUAL_PRODUCT_ID
      : ANDROID_ONBOARDING_MONTHLY_PRODUCT_ID;
  const { products } = await Purchases.getProducts({
    productIdentifiers: [productIdentifier],
    type: PRODUCT_CATEGORY.SUBSCRIPTION,
  });
  const product = products?.find(
    (candidate) => candidate.identifier === productIdentifier,
  ) ?? null;
  if (!product) throw new NativePurchaseConfigurationError();

  if (platform === 'ios') {
    if (hasIosIntroductoryOffer(product)) {
      throw new NativePurchaseConfigurationError();
    }
    const result = await Purchases.purchaseStoreProduct({ product });
    return result.customerInfo;
  }

  if (platform === 'android') {
    const subscriptionOption = tier === 'annual'
      ? findPlayPaidFirstTrialOption(product) ||
        getGooglePlayBasePlanOption(
          product,
          ANDROID_PAID_FIRST_ANNUAL_BASE_PLAN_ID,
        )
      : getGooglePlayBasePlanOption(
          product,
          ANDROID_ONBOARDING_MONTHLY_BASE_PLAN_ID,
        );
    if (!subscriptionOption) throw new NativePurchaseConfigurationError();
    const result = await Purchases.purchaseSubscriptionOption({
      subscriptionOption,
    });
    return result.customerInfo;
  }

  throw new NativePurchaseConfigurationError();
}

async function getAnnualLedOffering(
  revenueCat: RevenueCatModule,
): Promise<PurchasesOffering | null> {
  const { Purchases } = revenueCat;
  const offeringId = getAnnualLedOfferingId();
  let offerings = await Purchases.getOfferings();
  for (let attempt = 0; attempt < 2 && !offerings.all?.[offeringId]; attempt++) {
    await new Promise((resolve) => setTimeout(resolve, 350));
    offerings = await Purchases.getOfferings();
  }
  return offerings.all?.[offeringId] ?? null;
}

/**
 * Apple can briefly return the previous scheduled price for the trial SKU.
 * The approved no-trial $49.99 SKU is the localized same-price anchor, so a
 * trial package is safe to show only when StoreKit reports matching recurring
 * prices and currencies for both products.
 */
async function isIosAnnualTrialAtApprovedPrice(
  revenueCat: RevenueCatModule,
  annualTrialProduct: PurchasesStoreProduct | null | undefined,
): Promise<boolean> {
  if (
    annualTrialProduct?.identifier !== IOS_PAID_FIRST_RESCUE_ANNUAL_PRODUCT_ID ||
    !getIosFreeIntroDays(annualTrialProduct).includes(
      PAID_FIRST_RESCUE_TRIAL_DAYS,
    )
  ) {
    return false;
  }

  const paidFirstAnnualResult = await revenueCat.Purchases.getProducts({
    productIdentifiers: IOS_PAID_FIRST_ANNUAL_PRODUCT_IDS,
    type: revenueCat.PRODUCT_CATEGORY.SUBSCRIPTION,
  });
  const approvedPriceAnchor =
    paidFirstAnnualResult.products?.find((product) =>
      IOS_PAID_FIRST_ANNUAL_PRODUCT_IDS.includes(product.identifier),
    ) ?? null;
  if (
    !approvedPriceAnchor ||
    hasIosIntroductoryOffer(approvedPriceAnchor)
  ) {
    return false;
  }

  const trialPrice = annualTrialProduct.price;
  const anchorPrice = approvedPriceAnchor.price;
  const currenciesMatch =
    !annualTrialProduct.currencyCode ||
    !approvedPriceAnchor.currencyCode ||
    annualTrialProduct.currencyCode === approvedPriceAnchor.currencyCode;
  return (
    typeof trialPrice === 'number' &&
    typeof anchorPrice === 'number' &&
    trialPrice > 0 &&
    trialPrice === anchorPrice &&
    currenciesMatch &&
    Boolean(annualTrialProduct.priceString?.trim())
  );
}

/**
 * Store-verified native exit rescue for the paid-first onboarding arm.
 *
 * The rescue may render only when RevenueCat points the annual-led package at
 * the dedicated seven-day product, Apple reports the exact free phase, and
 * its recurring price matches the dedicated paid-first annual product. This
 * prevents a stale $97 trial product from sitting behind "$0 for 7 days,
 * then $49.99/year" copy while Apple's scheduled price change is pending.
 */
export async function getNativePaidFirstRescueQuote(
  userId: string,
): Promise<NativePaidFirstRescueQuote | null> {
  if (!isNativeBillingEnabled()) return null;
  const platform = getNativePlatform();
  if (platform !== 'ios' && platform !== 'android') return null;
  const configured = await ensureNativeBillingConfigured(userId);
  if (!configured) return null;
  const revenueCat = await loadRevenueCatModule();
  if (!revenueCat) return null;

  const { PACKAGE_TYPE, PRODUCT_CATEGORY, Purchases } = revenueCat;
  if (platform === 'android') {
    const { products } = await Purchases.getProducts({
      productIdentifiers: [ANDROID_PAID_FIRST_ANNUAL_PRODUCT_ID],
      type: PRODUCT_CATEGORY.SUBSCRIPTION,
    });
    const product = products?.find(
      (candidate) => isPlayStoreProduct(candidate, ANDROID_PAID_FIRST_ANNUAL_PRODUCT_ID, ANDROID_PAID_FIRST_ANNUAL_BASE_PLAN_ID),
    ) ?? null;
    const trialOption = findPlayPaidFirstTrialOption(product);
    const basePrice = googleBasePlanPrice(
      product,
      ANDROID_PAID_FIRST_ANNUAL_BASE_PLAN_ID,
    );
    if (!trialOption || !basePrice?.priceString?.trim()) return null;
    return {
      annualPriceString: stripZeroCents(basePrice.priceString.trim()),
      productIdentifier: ANDROID_PAID_FIRST_ANNUAL_PRODUCT_ID,
      trialDays: PAID_FIRST_RESCUE_TRIAL_DAYS,
    };
  }

  const offering = await getAnnualLedOffering(revenueCat);
  const rescuePackage = offering
    ? getPackageForTier(offering, 'annual', PACKAGE_TYPE)
    : null;
  const rescueProduct = rescuePackage?.product;
  if (
    !rescueProduct ||
    rescueProduct.identifier !== IOS_PAID_FIRST_RESCUE_ANNUAL_PRODUCT_ID ||
    !getIosFreeIntroDays(rescueProduct).includes(PAID_FIRST_RESCUE_TRIAL_DAYS)
  ) {
    return null;
  }

  const paidFirstAnnualResult = await Purchases.getProducts({
    productIdentifiers: IOS_PAID_FIRST_ANNUAL_PRODUCT_IDS,
    type: PRODUCT_CATEGORY.SUBSCRIPTION,
  });
  const paidFirstAnnualProduct =
    paidFirstAnnualResult.products?.find((product) =>
      IOS_PAID_FIRST_ANNUAL_PRODUCT_IDS.includes(product.identifier),
    ) ?? null;
  if (!paidFirstAnnualProduct || hasIosIntroductoryOffer(paidFirstAnnualProduct)) {
    return null;
  }

  const rescuePrice = rescueProduct.price;
  const paidFirstPrice = paidFirstAnnualProduct.price;
  const currenciesMatch =
    !rescueProduct.currencyCode ||
    !paidFirstAnnualProduct.currencyCode ||
    rescueProduct.currencyCode === paidFirstAnnualProduct.currencyCode;
  if (
    typeof rescuePrice !== 'number' ||
    typeof paidFirstPrice !== 'number' ||
    rescuePrice <= 0 ||
    rescuePrice !== paidFirstPrice ||
    !currenciesMatch ||
    !rescueProduct.priceString?.trim()
  ) {
    return null;
  }

  const eligibilityMap =
    await Purchases.checkTrialOrIntroductoryPriceEligibility({
      productIdentifiers: [IOS_PAID_FIRST_RESCUE_ANNUAL_PRODUCT_ID],
    });
  const eligibility = mapRevenueCatIntroEligibilityStatus(
    eligibilityMap[IOS_PAID_FIRST_RESCUE_ANNUAL_PRODUCT_ID]?.status,
  );
  if (eligibility === 'ineligible' || eligibility === 'no_offer') return null;

  return {
    annualPriceString: stripZeroCents(rescueProduct.priceString.trim()),
    productIdentifier: IOS_PAID_FIRST_RESCUE_ANNUAL_PRODUCT_ID,
    trialDays: PAID_FIRST_RESCUE_TRIAL_DAYS,
  };
}

function findPlayAnnualLedTrialOption(
  product: PurchasesStoreProduct | null | undefined,
): SubscriptionOption | null {
  const options = [
    ...(Array.isArray(product?.subscriptionOptions)
      ? product.subscriptionOptions
      : []),
    product?.defaultOption,
  ].filter(Boolean) as SubscriptionOption[];

  return (
    options.find((option) => {
      const optionId = typeof option?.id === 'string' ? option.id : '';
      const offerSegment = optionId.split(':')[1] ?? '';
      if (offerSegment !== PLAY_ANNUAL_LED_TRIAL_OFFER_ID) return false;
      const iso = String(
        option?.freePhase?.billingPeriod?.iso8601 ?? '',
      ).toUpperCase();
      return iso === `P${ANNUAL_LED_TRIAL_DAYS}D` || iso === 'P1W';
    }) || null
  );
}

function findPlayPaidFirstTrialOption(
  product: PurchasesStoreProduct | null | undefined,
): SubscriptionOption | null {
  if (!isPlayStoreProduct(product, ANDROID_PAID_FIRST_ANNUAL_PRODUCT_ID, ANDROID_PAID_FIRST_ANNUAL_BASE_PLAN_ID)) return null;
  const options = [
    ...(Array.isArray(product.subscriptionOptions)
      ? product.subscriptionOptions
      : []),
    product.defaultOption,
  ].filter(Boolean) as SubscriptionOption[];

  return options.find((option) => {
    const optionId = typeof option.id === 'string' ? option.id : '';
    const [basePlanId, offerId] = optionId.split(':');
    if (
      basePlanId !== ANDROID_PAID_FIRST_ANNUAL_BASE_PLAN_ID ||
      offerId !== PLAY_ANNUAL_LED_TRIAL_OFFER_ID
    ) {
      return false;
    }
    const iso = String(option?.freePhase?.billingPeriod?.iso8601 ?? '')
      .toUpperCase();
    return iso === `P${PAID_FIRST_RESCUE_TRIAL_DAYS}D` || iso === 'P1W';
  }) ?? null;
}

/**
 * Verified display facts for the annual-led paywall. Returns null when the
 * offering, products, or prices cannot be verified: surfaces fail closed and
 * render nothing rather than guessed prices or trial claims.
 * `annualTrialDays: null` is a VALID state (intro-ineligible user): the
 * paywall shows plain-price annual terms and checkout buys the base plan.
 */
export async function getAnnualLedQuote(
  userId: string,
): Promise<AnnualLedQuote | null> {
  if (!isNativeBillingEnabled()) return null;
  const configured = await ensureNativeBillingConfigured(userId);
  if (!configured) return null;
  const revenueCat = await loadRevenueCatModule();
  if (!revenueCat) return null;

  const { PACKAGE_TYPE } = revenueCat;
  const offering = await getAnnualLedOffering(revenueCat);
  if (!offering) return null;

  const annualPackage = getPackageForTier(offering, 'annual', PACKAGE_TYPE);
  const weeklyPackage = getPackageForTier(offering, 'weekly', PACKAGE_TYPE);
  const annualProduct = annualPackage?.product;
  const weeklyProduct = weeklyPackage?.product;

  const annualPriceString = annualProduct?.priceString?.trim();
  const weeklyPriceString = weeklyProduct?.priceString?.trim();
  const annualPrice = annualProduct?.price;
  const weeklyPrice = weeklyProduct?.price;
  if (
    !annualPriceString ||
    !weeklyPriceString ||
    typeof annualPrice !== 'number' ||
    typeof weeklyPrice !== 'number' ||
    annualPrice <= 0 ||
    weeklyPrice <= 0
  ) {
    return null;
  }

  let annualTrialDays: number | null = null;
  if (getNativePlatform() === 'ios') {
    if (
      annualProduct?.identifier !== IOS_ANNUAL_LED_ANNUAL_PRODUCT_ID ||
      weeklyProduct?.identifier !== IOS_PAID_FIRST_WEEKLY_PRODUCT_ID ||
      !getIosFreeIntroDays(annualProduct).includes(ANNUAL_LED_TRIAL_DAYS) ||
      hasIosIntroductoryOffer(weeklyProduct) ||
      !(await isIosAnnualTrialAtApprovedPrice(revenueCat, annualProduct))
    ) {
      // Wrong product or terms wired into the offering. Never sell a mystery
      // SKU or describe the weekly anchor as no-trial when StoreKit disagrees.
      return null;
    }
    const eligibilityMap =
      await revenueCat.Purchases.checkTrialOrIntroductoryPriceEligibility({
        productIdentifiers: [IOS_ANNUAL_LED_ANNUAL_PRODUCT_ID],
      });
    const eligibility = mapRevenueCatIntroEligibilityStatus(
      eligibilityMap[IOS_ANNUAL_LED_ANNUAL_PRODUCT_ID]?.status,
    );
    if (eligibility !== 'ineligible' && eligibility !== 'no_offer') {
      annualTrialDays = ANNUAL_LED_TRIAL_DAYS;
    }
  } else {
    // Play surfaces only eligibility-passing options, so a present trial-7d
    // option is both configured and available to this user.
    if (findPlayAnnualLedTrialOption(annualProduct)) {
      annualTrialDays = ANNUAL_LED_TRIAL_DAYS;
    } else if (!getGooglePlayBasePlanOption(annualProduct)) {
      // Neither the trial option nor a trial-free base plan resolved: the
      // ineligible fallback purchase would be impossible, so fail closed.
      return null;
    }
    if (!getGooglePlayBasePlanOption(weeklyProduct)) return null;
  }

  const currencyCode =
    annualProduct?.currencyCode || weeklyProduct?.currencyCode || null;

  return {
    annualPriceString: stripZeroCents(annualPriceString),
    annualPerWeekString: formatQuoteCurrency(annualPrice / 52, currencyCode),
    annualTrialDays,
    weeklyPriceString: stripZeroCents(weeklyPriceString),
    weeklyAnnualizedString: formatQuoteCurrency(weeklyPrice * 52, currencyCode),
    weeklyPerDayString: formatQuoteCurrency(weeklyPrice / 7, currencyCode),
    savingsPercent: Math.max(
      0,
      Math.round((1 - annualPrice / (weeklyPrice * 52)) * 100),
    ),
    rescueEligible: Boolean(await getNativeOnboardingYearlyOfferQuote(userId)),
  };
}

/** Full price of the store product or option a purchase charged, when known. */
export interface NativeChargedPrice {
  value: number;
  currency: string;
}

export interface AnnualLedPurchaseResult {
  customerInfo: CustomerInfo;
  /**
   * The full price of the exact product (iOS) or subscription option (Play)
   * this purchase bought. Null when the store did not report a usable price.
   * Analytics use it so an intro-ineligible purchase reports what the store
   * charged instead of a compiled constant.
   */
  chargedPrice: NativeChargedPrice | null;
}

function iosProductChargedPrice(
  product: PurchasesStoreProduct | null | undefined,
): NativeChargedPrice | null {
  if (
    typeof product?.price !== 'number' ||
    !(product.price > 0) ||
    !product.currencyCode
  ) {
    return null;
  }
  return { value: product.price, currency: product.currencyCode };
}

function playOptionChargedPrice(
  option: SubscriptionOption | null | undefined,
): NativeChargedPrice | null {
  const price = option?.fullPricePhase?.price;
  if (
    typeof price?.amountMicros !== 'number' ||
    !(price.amountMicros > 0) ||
    !price.currencyCode
  ) {
    return null;
  }
  return { value: price.amountMicros / 1_000_000, currency: price.currencyCode };
}

/**
 * Annual-led purchase paths. Annual with trial rides the explicit trial-7d
 * option on Play and the dedicated 7-day product on iOS; an intro-ineligible
 * user buys the plain base plan, matching the plain-price screen state.
 */
export async function purchaseAnnualLedPlan(
  userId: string,
  tier: 'annual' | 'weekly',
): Promise<CustomerInfo> {
  return (await purchaseAnnualLedPlanWithPrice(userId, tier)).customerInfo;
}

/** Same purchase as purchaseAnnualLedPlan, plus the price the store charged. */
export async function purchaseAnnualLedPlanWithPrice(
  userId: string,
  tier: 'annual' | 'weekly',
): Promise<AnnualLedPurchaseResult> {
  const configured = await ensureNativeBillingConfigured(userId);
  if (!configured) {
    throw new Error('Native in-app billing is not configured yet.');
  }
  const revenueCat = await loadRevenueCatModule();
  if (!revenueCat) {
    throw new Error('Native in-app billing is unavailable in this environment.');
  }
  const { PACKAGE_TYPE, Purchases } = revenueCat;
  await assertRevenueCatIdentity(Purchases, userId);
  await syncRevenueCatAttributionIdentifiers(userId, Purchases, { force: true });

  const offering = await getAnnualLedOffering(revenueCat);
  const targetPackage = offering
    ? getPackageForTier(offering, tier, PACKAGE_TYPE)
    : null;
  const targetProduct = targetPackage?.product;
  if (!targetPackage || !targetProduct) {
    throw new NativePurchaseConfigurationError();
  }

  if (getNativePlatform() === 'ios') {
    if (tier === 'annual') {
      if (
        targetProduct.identifier !== IOS_ANNUAL_LED_ANNUAL_PRODUCT_ID ||
        !(await isIosAnnualTrialAtApprovedPrice(revenueCat, targetProduct))
      ) {
        throw new NativePurchaseConfigurationError();
      }
      const result = await Purchases.purchasePackage({
        aPackage: targetPackage,
      });
      return {
        customerInfo: result.customerInfo,
        chargedPrice: iosProductChargedPrice(targetProduct),
      };
    }
    if (targetProduct.identifier !== IOS_PAID_FIRST_WEEKLY_PRODUCT_ID) {
      throw new NativePurchaseConfigurationError();
    }
    const result = await Purchases.purchasePackage({ aPackage: targetPackage });
    return {
      customerInfo: result.customerInfo,
      chargedPrice: iosProductChargedPrice(targetProduct),
    };
  }

  if (tier === 'annual') {
    const trialOption = findPlayAnnualLedTrialOption(targetProduct);
    if (trialOption) {
      const result = await Purchases.purchaseSubscriptionOption({
        subscriptionOption: trialOption,
      });
      return {
        customerInfo: result.customerInfo,
        chargedPrice: playOptionChargedPrice(trialOption),
      };
    }
    // Intro-ineligible: buy the verified trial-free base plan at plain price.
    const basePlan = getGooglePlayBasePlanOption(targetProduct);
    if (!basePlan) throw new NativePurchaseConfigurationError();
    const result = await Purchases.purchaseSubscriptionOption({
      subscriptionOption: basePlan,
    });
    return {
      customerInfo: result.customerInfo,
      chargedPrice: playOptionChargedPrice(basePlan),
    };
  }

  const weeklyBasePlan = getGooglePlayBasePlanOption(targetProduct);
  if (!weeklyBasePlan) throw new NativePurchaseConfigurationError();
  const result = await Purchases.purchaseSubscriptionOption({
    subscriptionOption: weeklyBasePlan,
  });
  return {
    customerInfo: result.customerInfo,
    chargedPrice: playOptionChargedPrice(weeklyBasePlan),
  };
}

/** Purchase the paid-first exit rescue only while the verified 7-day quote exists. */
export async function purchaseNativePaidFirstRescueTrial(
  userId: string,
): Promise<CustomerInfo> {
  const quote = await getNativePaidFirstRescueQuote(userId);
  if (!quote) {
    throw new Error('This 7-day trial is not available for this account.');
  }
  if (getNativePlatform() === 'android') {
    const configured = await ensureNativeBillingConfigured(userId);
    if (!configured) throw new NativePurchaseConfigurationError();
    const revenueCat = await loadRevenueCatModule();
    if (!revenueCat) throw new NativePurchaseConfigurationError();
    const { PRODUCT_CATEGORY, Purchases } = revenueCat;
    await assertRevenueCatIdentity(Purchases, userId);
    await syncRevenueCatAttributionIdentifiers(userId, Purchases, { force: true });
    const { canMakePayments } = await Purchases.canMakePayments();
    if (!canMakePayments) {
      throw new Error('In-app purchases are unavailable on this device.');
    }
    const { products } = await Purchases.getProducts({
      productIdentifiers: [ANDROID_PAID_FIRST_ANNUAL_PRODUCT_ID],
      type: PRODUCT_CATEGORY.SUBSCRIPTION,
    });
    const product = products?.find(
      (candidate) => isPlayStoreProduct(candidate, ANDROID_PAID_FIRST_ANNUAL_PRODUCT_ID, ANDROID_PAID_FIRST_ANNUAL_BASE_PLAN_ID),
    ) ?? null;
    const trialOption = findPlayPaidFirstTrialOption(product);
    if (!trialOption) throw new NativePurchaseConfigurationError();
    const result = await Purchases.purchaseSubscriptionOption({
      subscriptionOption: trialOption,
    });
    return result.customerInfo;
  }
  return purchaseAnnualLedPlan(userId, 'annual');
}

/**
 * True when a store product is the given Play subscription base plan. RevenueCat
 * 6+ reports each base plan as its own product, `<productId>:<basePlanId>`;
 * the bare product id is accepted too.
 */
function isPlayStoreProduct(
  product: PurchasesStoreProduct | null | undefined,
  productId: string,
  basePlanId: string,
): boolean {
  const identifier = product?.identifier;
  return identifier === productId || identifier === `${productId}:${basePlanId}`;
}

/**
 * RevenueCat configured for this member on iOS or Android, for the VIP and
 * Pro purchase paths in nativeVipPro.ts. Null on web, with native billing
 * off, or when the SDK cannot be configured.
 */
export async function loadNativeRevenueCatForUser(
  userId: string,
): Promise<{ revenueCat: RevenueCatModule; platform: 'ios' | 'android' } | null> {
  const platform = getNativePlatform();
  if (!isNativeBillingEnabled() || (platform !== 'ios' && platform !== 'android')) return null;
  if (!(await ensureNativeBillingConfigured(userId))) return null;
  const revenueCat = await loadRevenueCatModule();
  if (!revenueCat) return null;
  return { revenueCat, platform };
}

/**
 * The checks every native purchase runs right before the store sheet opens:
 * the SDK is signed in as this member, attribution is current, and the
 * device can pay.
 */
export async function prepareNativeStorePurchase(
  userId: string,
  Purchases: RevenueCatModule['Purchases'],
): Promise<void> {
  await assertRevenueCatIdentity(Purchases, userId);
  await syncRevenueCatAttributionIdentifiers(userId, Purchases, { force: true });
  const { canMakePayments } = await Purchases.canMakePayments();
  if (!canMakePayments) throw new Error('In-app purchases are unavailable on this device.');
}

/**
 * Play reports subscriptions as `<productId>:<basePlanId>` (RevenueCat 6+).
 * Google's product-change API wants the bare product id.
 */
export function stripPlayBasePlanSuffix(productIdentifier: string): string {
  const idx = productIdentifier.indexOf(':');
  return idx === -1 ? productIdentifier : productIdentifier.slice(0, idx);
}

/**
 * Canonical store name for the current native runtime, derived from the
 * platform rather than the RevenueCat snapshot (which can be uppercase or
 * null). Returns null on web.
 */
export function canonicalNativeStore(): 'app_store' | 'play_store' | null {
  const platform = getNativePlatform();
  if (platform === 'ios') return 'app_store';
  if (platform === 'android') return 'play_store';
  return null;
}

// ── Legacy limited $67 first-year Annual offer ──
//
// The offer lives in its OWN RevenueCat offering backed by dedicated store
// products (base $97/yr plus a first-year introductory price). It must never
// be attached to the standard annual product: Apple auto-applies introductory
// offers to every eligible purchase of a product. The primary onboarding
// paywall does not request this offering; only explicit save, win-back, or
// limited promotional surfaces should opt into it.

const DEFAULT_ONBOARDING_YEARLY_OFFERING_ID = 'onboarding-yearly-offer';
/**
 * Onboarding v3 exit offer (Alex, Oct 6): $29.99 for year one, renewing at $97, shown
 * once after a member closes the store checkout without buying. Its own
 * offering and products, so the $67 save offer above is untouched.
 */
export const ONBOARDING_EXIT_OFFERING_ID = 'onboarding-exit-offer';
export const NATIVE_ANNUAL_EXIT29_PRODUCT_ID = 'jp_premium_annual_offer29';

export function getOnboardingYearlyOfferingId(): string {
  const configured = env.revenueCatOnboardingYearlyOfferingId;
  return configured?.trim() || DEFAULT_ONBOARDING_YEARLY_OFFERING_ID;
}

export interface NativeOnboardingYearlyOfferQuote {
  /** Localized first-year price from the store, e.g. "$67.00". */
  introPriceString: string;
  /** Localized recurring price the plan renews at, e.g. "$97.00". */
  renewalPriceString: string;
  /** First-year price divided by 12 in the store currency, e.g. "$2.50". Null when unknown. */
  introPricePerMonthString: string | null;
  /** Numeric first-year price and its ISO currency, for conversion tracking. */
  introPriceAmount: number | null;
  currencyCode: string | null;
  productIdentifier: string;
  /**
   * First-year savings vs the SAME product's renewal price ((renewal -
   * intro) / renewal), computed from the two numeric store prices of one
   * product so it is true in every storefront currency and never references
   * a plan the shopper was not shown. Null when the math cannot be trusted.
   */
  savingsPercent: number | null;
}

async function getOnboardingYearlyOfferPackage(
  revenueCat: RevenueCatModule,
  offeringIdOverride?: string,
): Promise<{ offerPackage: PurchasesPackage | null }> {
  const { PACKAGE_TYPE, Purchases } = revenueCat;
  const offeringId = offeringIdOverride || getOnboardingYearlyOfferingId();

  // getOfferings can briefly return an empty catalog while RevenueCat is still
  // fetching right after an identity change (the useAuth churn refetch). That
  // showed up as intermittent offering_or_package_missing that hid the offer on
  // an otherwise-valid load. Retry a couple of times so an in-flight fetch has
  // a chance to settle before we give up.
  let offerings = await Purchases.getOfferings();
  for (let attempt = 0; attempt < 2 && !offerings.all?.[offeringId]; attempt++) {
    await new Promise((resolve) => setTimeout(resolve, 350));
    offerings = await Purchases.getOfferings();
  }

  const offering = offerings.all?.[offeringId] ?? null;
  return {
    offerPackage: offering ? getPackageForTier(offering, 'annual', PACKAGE_TYPE) : null,
  };
}

/**
 * Deal framing derived from one product's own store prices: the percent the
 * first year saves against the price it renews at. Anchoring to the renewal
 * price keeps the claim inside what the shopper actually sees (the $97 plan
 * they just declined and the price printed under the offer), never a plan
 * that is not on their screen. Null whenever the math cannot be trusted so
 * the UI degrades to no claim, never to a wrong number.
 */
function computeOfferSavings(
  offerProduct: PurchasesPackage['product'] | undefined
): { savingsPercent: number | null } {
  const none = { savingsPercent: null };
  const introPrice: number | undefined = offerProduct?.introPrice?.price;
  const renewalPrice: number | undefined = offerProduct?.price;
  if (
    typeof introPrice !== 'number' ||
    typeof renewalPrice !== 'number' ||
    introPrice <= 0 ||
    renewalPrice <= 0 ||
    introPrice >= renewalPrice
  ) {
    return none;
  }
  return {
    savingsPercent: Math.round(
      ((renewalPrice - introPrice) / renewalPrice) * 100,
    ),
  };
}

/**
 * Availability + localized pricing for the onboarding yearly offer, or null
 * when it must stay hidden (no offering configured, no intro price, or an
 * explicit iOS ineligible/no-offer verdict). Unknown eligibility still shows
 * the offer because fresh installs often have no receipt to evaluate. Prices
 * come from the store so non-USD storefronts show localized amounts.
 */
export const NATIVE_ANNUAL_OFFER67_PRODUCT_ID = 'jp_premium_annual_offer67';
const PLAY_ANNUAL_OFFER67_OFFER_ID = 'first-year-67';

/** True when the identifier is the $67 first-year annual product on either store. */
export function isNativeAnnualOffer67ProductId(
  productIdentifier: string | null | undefined,
): boolean {
  if (!productIdentifier) return false;
  return stripPlayBasePlanSuffix(productIdentifier) === NATIVE_ANNUAL_OFFER67_PRODUCT_ID;
}

export interface NativeOnboardingYearlyOfferQuoteOptions {
  /**
   * 'onboarding' (default): an unknown iOS verdict still shows the offer,
   * because fresh installs often have no receipt to evaluate.
   * 'rescue': the shopper is an EXISTING subscriber, so a receipt exists and
   * Apple grants introductory offers once per subscription group. Only an
   * explicit `eligible` verdict may show the price; anything else hides it
   * so the sheet can never promise $67 and let the App Store charge $97.
   */
  iosEligibility?: 'onboarding' | 'rescue';
  /** RevenueCat offering to read; defaults to the $67 onboarding-yearly-offer. */
  offeringId?: string;
}

/** The first-year price per month in the store's currency, or null when it can't be formatted honestly. */
function formatIntroPerMonth(product: PurchasesPackage['product'] | undefined): string | null {
  const intro = product?.introPrice?.price;
  const currency = product?.currencyCode;
  if (typeof intro !== 'number' || intro <= 0 || !currency) return null;
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Math.round((intro / 12) * 100) / 100);
  } catch {
    return null;
  }
}

export async function getNativeOnboardingYearlyOfferQuote(
  userId: string,
  options: NativeOnboardingYearlyOfferQuoteOptions = {},
): Promise<NativeOnboardingYearlyOfferQuote | null> {
  // Every hidden-offer path reports WHY to analytics: the offer self-hides by
  // design when config is missing, which makes silent failures (store product
  // not propagated, offering misnamed, eligibility surprises) look identical
  // to "working as intended" from a screenshot.
  const hide = (reason: string, extra?: Record<string, unknown>): null => {
    void trackEvent('paywall_offer_unavailable', {
      offer_kind: 'onboarding_yearly',
      platform: getNativePlatform(),
      reason,
      ...extra,
    });
    return null;
  };

  if (!isNativeBillingEnabled()) return hide('billing_disabled');

  const configured = await ensureNativeBillingConfigured(userId);
  if (!configured) return hide('not_configured');

  const revenueCat = await loadRevenueCatModule();
  if (!revenueCat) return hide('rc_module_unavailable');

  const { offerPackage } = await getOnboardingYearlyOfferPackage(revenueCat, options.offeringId);
  const product = offerPackage?.product;
  const productIdentifier: string | undefined = product?.identifier;
  const introPriceString: string | undefined = product?.introPrice?.priceString;
  const renewalPriceString: string | undefined = product?.priceString;
  if (!offerPackage) {
    return hide('offering_or_package_missing', { offering_id: options.offeringId || getOnboardingYearlyOfferingId() });
  }
  if (!productIdentifier || !introPriceString || !renewalPriceString) {
    return hide('intro_price_missing', { product_identifier: productIdentifier ?? null });
  }
  const { savingsPercent } = computeOfferSavings(product);

  if (getNativePlatform() === 'ios') {
    const eligibilityMap = await revenueCat.Purchases.checkTrialOrIntroductoryPriceEligibility({
      productIdentifiers: [productIdentifier],
    });
    const eligibility = mapRevenueCatIntroEligibilityStatus(
      eligibilityMap[productIdentifier]?.status
    );
    // Fresh installs commonly return unknown because there is no receipt yet.
    // Explicit ineligible/no-offer verdicts are different: showing $67 after
    // either verdict would let the App Store sheet charge the $97 base price.
    if (eligibility === 'ineligible' || eligibility === 'no_offer') {
      return hide(`ios_intro_${eligibility}`, { product_identifier: productIdentifier });
    }
    if (options.iosEligibility === 'rescue' && eligibility !== 'eligible') {
      return hide(`ios_intro_${eligibility}_rescue`, { product_identifier: productIdentifier });
    }
  }

  return {
    introPriceString: stripZeroCents(introPriceString),
    renewalPriceString: stripZeroCents(renewalPriceString),
    introPricePerMonthString: formatIntroPerMonth(product),
    introPriceAmount: typeof product?.introPrice?.price === 'number' ? product.introPrice.price : null,
    currencyCode: product?.currencyCode ?? null,
    productIdentifier,
    savingsPercent,
  };
}

export interface PurchaseNativeOnboardingYearlyOfferOptions {
  /**
   * Native save-offer rescue: the subscriber's current product. On Play this
   * becomes a real plan change to jp_premium_annual_offer67:p1y with the
   * first-year-67 offer selected explicitly (googleProductChangeInfo, charge
   * the discounted year now, Google carries the remaining time). On iOS
   * Apple decides upgrade/downgrade timing from the subscription group ranks
   * and the value is only validated. Implies strict iOS eligibility.
   */
  replaceProductId?: string | null;
  /** RevenueCat offering to buy from; defaults to the $67 onboarding-yearly-offer. */
  offeringId?: string;
}

/** The Play subscription option carrying the paid first-year-67 phase. */
function getPlayAnnualOffer67Option(
  product: PurchasesStoreProduct | null | undefined,
): SubscriptionOption | null {
  const options = [
    ...(Array.isArray(product?.subscriptionOptions)
      ? product.subscriptionOptions
      : []),
    product?.defaultOption,
  ].filter(Boolean) as SubscriptionOption[];
  return (
    options.find((option) => {
      const optionId = typeof option?.id === 'string' ? option.id : '';
      return (
        optionId.split(':')[1] === PLAY_ANNUAL_OFFER67_OFFER_ID &&
        Boolean(option?.introPhase) &&
        !option?.freePhase
      );
    }) || null
  );
}

export async function purchaseNativeOnboardingYearlyOffer(
  userId: string,
  options: PurchaseNativeOnboardingYearlyOfferOptions = {},
): Promise<CustomerInfo> {
  const replaceProductId = options.replaceProductId?.trim() || null;
  if (replaceProductId && isNativeAnnualOffer67ProductId(replaceProductId)) {
    // Already on the product we would switch to; nothing to buy.
    throw new NativePurchaseConfigurationError();
  }
  const configured = await ensureNativeBillingConfigured(userId);
  if (!configured) {
    throw new Error('Native in-app billing is not configured yet.');
  }

  const revenueCat = await loadRevenueCatModule();
  if (!revenueCat) {
    throw new Error('Native in-app billing is unavailable in this environment.');
  }

  const { Purchases } = revenueCat;
  await assertRevenueCatIdentity(Purchases, userId);
  await syncRevenueCatAttributionIdentifiers(userId, Purchases, { force: true });

  const { canMakePayments } = await Purchases.canMakePayments();
  if (!canMakePayments) {
    throw new Error('In-app purchases are unavailable on this device.');
  }

  const { offerPackage } = await getOnboardingYearlyOfferPackage(revenueCat, options.offeringId);
  if (!offerPackage) {
    // No fallback to the standard annual product: it would charge a different
    // price than the one on screen.
    throw new Error('This offer is not available right now.');
  }

  const productIdentifier: string | undefined = offerPackage.product?.identifier;
  const expectedProduct =
    options.offeringId === ONBOARDING_EXIT_OFFERING_ID ? NATIVE_ANNUAL_EXIT29_PRODUCT_ID : NATIVE_ANNUAL_OFFER67_PRODUCT_ID;
  if (!productIdentifier || stripPlayBasePlanSuffix(productIdentifier) !== expectedProduct) {
    // The offering must resolve to its own dedicated offer product on this store.
    throw new NativePurchaseConfigurationError();
  }
  if (getNativePlatform() === 'ios') {
    const eligibilityMap = await Purchases.checkTrialOrIntroductoryPriceEligibility({
      productIdentifiers: [productIdentifier],
    });
    const eligibility = mapRevenueCatIntroEligibilityStatus(
      eligibilityMap[productIdentifier]?.status
    );
    if (eligibility === 'ineligible' || eligibility === 'no_offer') {
      throw new Error('This new member offer is not available for this Apple ID.');
    }
    if (replaceProductId && eligibility !== 'eligible') {
      // Existing subscriber: never let an unknown verdict charge $97 under
      // a $67 promise.
      throw new Error('This offer is not available for this Apple ID.');
    }
    const result = await Purchases.purchasePackage({ aPackage: offerPackage });
    return result.customerInfo;
  }

  if (replaceProductId) {
    // Rescue switch on Play: replace the current subscription with the $67
    // first-year option instead of buying a second one. Charge the year now;
    // Google adds the prorated remainder of the old period on top.
    const offerOption = getPlayAnnualOffer67Option(offerPackage.product);
    const prorationMode = revenueCat.PRORATION_MODE?.IMMEDIATE_AND_CHARGE_FULL_PRICE;
    if (!offerOption || typeof prorationMode !== 'number') {
      throw new NativePurchaseConfigurationError();
    }
    const result = await Purchases.purchaseSubscriptionOption({
      subscriptionOption: offerOption,
      googleProductChangeInfo: {
        oldProductIdentifier: stripPlayBasePlanSuffix(replaceProductId),
        prorationMode,
      },
    });
    return result.customerInfo;
  }

  const result = await Purchases.purchasePackage({ aPackage: offerPackage });
  return result.customerInfo;
}

export async function openNativeSubscriptionManagementUrl(url: string): Promise<void> {
  if (isNativePlatform()) {
    await Browser.open({ url });
    return;
  }

  if (typeof window !== 'undefined') {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

export function extractNativeSubscriptionSnapshot(
  customerInfo: CustomerInfo | null
): NativeSubscriptionSnapshot {
  if (!customerInfo) {
    return {
      isActive: false,
      tier: 'free',
      status: 'canceled',
      billingTier: null,
      currentPeriodEnd: null,
      cancelAtPeriodEnd: false,
      managementUrl: null,
      store: null,
      productIdentifier: null,
    };
  }

  const entitlementId = getNativeEntitlementId();
  const activeEntitlement = customerInfo.entitlements.active[entitlementId] || null;
  const configuredEntitlement = customerInfo.entitlements.all[entitlementId] || null;
  const activeSubscriptions = customerInfo.activeSubscriptions || [];
  const fallbackActiveProductIdentifier =
    activeSubscriptions.find((productId) => isKnownNativePremiumProductId(productId)) ||
    activeSubscriptions[0] ||
    null;
  const fallbackPeriodStillActive = hasFutureExpiration(customerInfo.latestExpirationDate);

  // If the configured entitlement is stale/misaligned but the store reports an active
  // subscription, prefer the active subscription so paid users are not gated.
  if (!activeEntitlement && fallbackActiveProductIdentifier && fallbackPeriodStillActive) {
    return {
      isActive: true,
      tier: 'premium',
      status: 'active',
      billingTier: inferBillingTierFromProductId(fallbackActiveProductIdentifier),
      currentPeriodEnd: customerInfo.latestExpirationDate,
      cancelAtPeriodEnd: false,
      managementUrl: customerInfo.managementURL || null,
      store: null,
      productIdentifier: fallbackActiveProductIdentifier,
      periodType: null,
    };
  }

  const entitlement = activeEntitlement || configuredEntitlement;
  if (!entitlement) {
    return {
      isActive: false,
      tier: 'free',
      status: 'canceled',
      billingTier: null,
      currentPeriodEnd: customerInfo.latestExpirationDate,
      cancelAtPeriodEnd: false,
      managementUrl: customerInfo.managementURL || null,
      store: null,
      productIdentifier: null,
    };
  }

  const isActive = Boolean(activeEntitlement?.isActive);
  const status = getSubscriptionStatusFromEntitlement(
    isActive,
    entitlement.periodType,
    Boolean(entitlement.billingIssueDetectedAt)
  );
  const tier = !isActive
    ? 'free'
    : (entitlement.periodType || '').toUpperCase() === 'TRIAL'
      ? 'trialing'
      : 'premium';

  return {
    isActive,
    tier,
    status,
    billingTier: inferBillingTierFromProductId(entitlement.productIdentifier),
    currentPeriodEnd: entitlement.expirationDate,
    cancelAtPeriodEnd: isActive ? !entitlement.willRenew : false,
    managementUrl: customerInfo.managementURL || null,
    store: entitlement.store || null,
    productIdentifier: entitlement.productIdentifier || null,
    periodType: typeof entitlement.periodType === 'string' && entitlement.periodType
      ? entitlement.periodType
      : null,
  };
}
